import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

// Lazy load the coverage map
const CoverageMap = lazy(() => import('../../components/CoverageMap/CoverageMap'));
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { TrustScoreRing, TrustScoreBreakdown } from '../../components/TrustScore/TrustScore';
import { QuoteActivityChart, QuoteHeatMap, ProfileViewsChart } from '../../components/Analytics/AnalyticsCharts';
import ComplaintsTab from './ComplaintsTab';
import OverviewTab from './OverviewTab';
import ProfileTab from './ProfileTab';
import SettingsTab from './SettingsTab';
import QuotesTab from './QuotesTab';
import RoutesTab from './RoutesTab';
import AnalyticsTab from './AnalyticsTab';
import MileageLogTab from './MileageLogTab';
import { InvoicesTab } from './Invoices';
import MobileRoutesPage from '../Mobile/MobileRoutesPage';
import MobileMileagePage from '../Mobile/MobileMileagePage';
import api from '../../lib/api';
import { toast } from 'sonner';
import { LayoutDashboard, User, MessageSquare, Settings, MapPin, DollarSign, Eye, Star, Clock, Loader2, CheckCircle, Plus, Trash2, Award, Edit2, Mail, TrendingUp, MapPinned, Camera, X, CheckSquare, Copy, Link as LinkIcon, FileText, Shield, AlertTriangle, Bell, Upload, Phone, Flag, UserCheck, Route, Receipt, Lock, Crown, BarChart3, Car } from 'lucide-react';

function DashboardPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var isAuthenticated = auth.isAuthenticated;
  var authLoading = auth.loading;
  var searchParamsHook = useSearchParams();
  var searchParams = searchParamsHook[0];
  var location = useLocation();
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var profileState = useState(null);
  var profile = profileState[0];
  var setProfile = profileState[1];
  
  var analyticsState = useState(null);
  var analytics = analyticsState[0];
  var setAnalytics = analyticsState[1];
  
  var quotesState = useState([]);
  var quotes = quotesState[0];
  var setQuotes = quotesState[1];
  
  var activeTabState = useState(function() {
    // Initialize from URL on first load
    var params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'overview';
  });
  var activeTab = activeTabState[0];
  var setActiveTabInternal = activeTabState[1];
  
  // Watch for URL parameter changes (e.g., when navigating from Settings link)
  // Using location.search ensures we catch all URL changes including same-page navigation
  useEffect(function() {
    var params = new URLSearchParams(location.search);
    var tabFromUrl = params.get('tab');
    // Always update if URL has a tab parameter - this handles repeated navigation to same tab
    if (tabFromUrl) {
      setActiveTabInternal(tabFromUrl);
      // Clean up the URL by removing the _t parameter if present
      if (params.has('_t')) {
        window.history.replaceState(null, '', '/dashboard?tab=' + tabFromUrl);
      }
    }
  }, [location.search, location.key]); // location.key changes on each navigation
  
  // Custom setActiveTab that also updates URL
  var setActiveTab = function(tab) {
    setActiveTabInternal(tab);
    // Update URL without full page reload
    var newUrl = '/dashboard?tab=' + tab;
    window.history.replaceState(null, '', newUrl);
  };
  
  var showSubRequiredState = useState(false);
  var showSubRequired = showSubRequiredState[0];
  var setShowSubRequired = showSubRequiredState[1];
  
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  
  // Profile form state
  var displayNameState = useState('');
  var displayName = displayNameState[0];
  var setDisplayName = displayNameState[1];
  
  var companyNameState = useState('');
  var companyName = companyNameState[0];
  var setCompanyName = companyNameState[1];
  
  var phoneState = useState('');
  var phone = phoneState[0];
  var setPhone = phoneState[1];
  
  var emailState = useState('');
  var email = emailState[0];
  var setEmail = emailState[1];
  
  var bioState = useState('');
  var bio = bioState[0];
  var setBio = bioState[1];
  
  var websiteState = useState('');
  var website = websiteState[0];
  var setWebsite = websiteState[1];
  
  var yearsExpState = useState('');
  var yearsExp = yearsExpState[0];
  var setYearsExp = yearsExpState[1];
  
  var responseGuaranteeState = useState('');
  var responseGuarantee = responseGuaranteeState[0];
  var setResponseGuarantee = responseGuaranteeState[1];
  
  // Photo upload state
  var photoUrlState = useState(null);
  var photoUrl = photoUrlState[0];
  var setPhotoUrl = photoUrlState[1];
  
  var uploadingPhotoState = useState(false);
  var uploadingPhoto = uploadingPhotoState[0];
  var setUploadingPhoto = uploadingPhotoState[1];
  
  // Subscription limits state
  var subscriptionLimitsState = useState(null);
  var subscriptionLimits = subscriptionLimitsState[0];
  var setSubscriptionLimits = subscriptionLimitsState[1];
  
  // Downgrade modal state
  var showDowngradeModalState = useState(false);
  var showDowngradeModal = showDowngradeModalState[0];
  var setShowDowngradeModal = showDowngradeModalState[1];
  
  var downgradeDataState = useState(null);
  var downgradeData = downgradeDataState[0];
  var setDowngradeData = downgradeDataState[1];
  
  var selectedDowngradeAreasState = useState([]);
  var selectedDowngradeAreas = selectedDowngradeAreasState[0];
  var setSelectedDowngradeAreas = selectedDowngradeAreasState[1];
  
  var processingDowngradeState = useState(false);
  var processingDowngrade = processingDowngradeState[0];
  var setProcessingDowngrade = processingDowngradeState[1];
  
  // Coverage area form state
  var coverageTypeState = useState('county');
  var coverageType = coverageTypeState[0];
  var setCoverageType = coverageTypeState[1];
  
  var coverageStateState = useState('');
  var coverageStateVal = coverageStateState[0];
  var setCoverageStateVal = coverageStateState[1];
  
  var coverageCityState = useState('');
  var coverageCity = coverageCityState[0];
  var setCoverageCity = coverageCityState[1];
  
  var coverageCountyState = useState('');
  var coverageCounty = coverageCountyState[0];
  var setCoverageCounty = coverageCountyState[1];
  
  var coverageZipCodesState = useState('');
  var coverageZipCodes = coverageZipCodesState[0];
  var setCoverageZipCodes = coverageZipCodesState[1];
  
  var coverageFeeState = useState(75);
  var coverageFee = coverageFeeState[0];
  var setCoverageFee = coverageFeeState[1];
  
  var coverageAttemptsState = useState(3);
  var coverageAttempts = coverageAttemptsState[0];
  var setCoverageAttempts = coverageAttemptsState[1];

  // Pricing type for new coverage areas
  var coveragePricingTypeState = useState('fixed');
  var coveragePricingType = coveragePricingTypeState[0];
  var setCoveragePricingType = coveragePricingTypeState[1];
  
  var coverageMinFeeState = useState(50);
  var coverageMinFee = coverageMinFeeState[0];
  var setCoverageMinFee = coverageMinFeeState[1];
  
  var coverageMaxFeeState = useState(150);
  var coverageMaxFee = coverageMaxFeeState[0];
  var setCoverageMaxFee = coverageMaxFeeState[1];
  
  // Per-area rush and same day fees
  var coverageRushEnabledState = useState(false);
  var coverageRushEnabled = coverageRushEnabledState[0];
  var setCoverageRushEnabled = coverageRushEnabledState[1];
  
  var coverageRushFeeState = useState(35);
  var coverageRushFee = coverageRushFeeState[0];
  var setCoverageRushFee = coverageRushFeeState[1];
  
  var coverageRushHoursState = useState(24);
  var coverageRushHours = coverageRushHoursState[0];
  var setCoverageRushHours = coverageRushHoursState[1];
  
  var coverageSameDayEnabledState = useState(false);
  var coverageSameDayEnabled = coverageSameDayEnabledState[0];
  var setCoverageSameDayEnabled = coverageSameDayEnabledState[1];
  
  var coverageSameDayFeeState = useState(75);
  var coverageSameDayFee = coverageSameDayFeeState[0];
  var setCoverageSameDayFee = coverageSameDayFeeState[1];
  
  // Apply to All modal
  var showApplyAllModalState = useState(false);
  var showApplyAllModal = showApplyAllModalState[0];
  var setShowApplyAllModal = showApplyAllModalState[1];
  
  var applyAllRushEnabledState = useState(false);
  var applyAllRushEnabled = applyAllRushEnabledState[0];
  var setApplyAllRushEnabled = applyAllRushEnabledState[1];
  
  var applyAllRushFeeState = useState(35);
  var applyAllRushFee = applyAllRushFeeState[0];
  var setApplyAllRushFee = applyAllRushFeeState[1];
  
  var applyAllRushHoursState = useState(24);
  var applyAllRushHours = applyAllRushHoursState[0];
  var setApplyAllRushHours = applyAllRushHoursState[1];
  
  var applyAllSameDayEnabledState = useState(false);
  var applyAllSameDayEnabled = applyAllSameDayEnabledState[0];
  var setApplyAllSameDayEnabled = applyAllSameDayEnabledState[1];
  
  var applyAllSameDayFeeState = useState(75);
  var applyAllSameDayFee = applyAllSameDayFeeState[0];
  var setApplyAllSameDayFee = applyAllSameDayFeeState[1];
  
  // Flexible pricing state - all optional with toggles
  var enableRushState = useState(true);
  var enableRush = enableRushState[0];
  var setEnableRush = enableRushState[1];
  
  var rushFeeState = useState(35);
  var rushFee = rushFeeState[0];
  var setRushFee = rushFeeState[1];
  
  var rushHoursState = useState(48);
  var rushHours = rushHoursState[0];
  var setRushHours = rushHoursState[1];
  
  var enableSameDayState = useState(true);
  var enableSameDay = enableSameDayState[0];
  var setEnableSameDay = enableSameDayState[1];
  
  var sameDayFeeState = useState(75);
  var sameDayFee = sameDayFeeState[0];
  var setSameDayFee = sameDayFeeState[1];
  
  var enableMileageState = useState(true);
  var enableMileage = enableMileageState[0];
  var setEnableMileage = enableMileageState[1];
  
  var mileageRateState = useState(0.65);
  var mileageRate = mileageRateState[0];
  var setMileageRate = mileageRateState[1];
  
  var freeMilesState = useState(10);
  var freeMiles = freeMilesState[0];
  var setFreeMiles = freeMilesState[1];
  
  var enableAdditionalAttemptState = useState(true);
  var enableAdditionalAttempt = enableAdditionalAttemptState[0];
  var setEnableAdditionalAttempt = enableAdditionalAttemptState[1];
  
  var additionalFeeState = useState(35);
  var additionalFee = additionalFeeState[0];
  var setAdditionalFee = additionalFeeState[1];
  
  // Multi-serve discount state
  var enableMultiServeState = useState(false);
  var enableMultiServe = enableMultiServeState[0];
  var setEnableMultiServe = enableMultiServeState[1];
  
  var discountTypeState = useState('percentage');
  var discountType = discountTypeState[0];
  var setDiscountType = discountTypeState[1];
  
  var discountAmountState = useState(50);
  var discountAmount = discountAmountState[0];
  var setDiscountAmount = discountAmountState[1];
  
  // Printing config state
  var enablePrintingState = useState(false);
  var enablePrinting = enablePrintingState[0];
  var setEnablePrinting = enablePrintingState[1];
  
  var freePagesState = useState(0);
  var freePages = freePagesState[0];
  var setFreePages = freePagesState[1];
  
  var perPageFeeState = useState(0.25);
  var perPageFee = perPageFeeState[0];
  var setPerPageFee = perPageFeeState[1];
  
  // Server address state
  var addressTypeState = useState('business');
  var addressType = addressTypeState[0];
  var setAddressType = addressTypeState[1];
  
  var addressStreetState = useState('');
  var addressStreet = addressStreetState[0];
  var setAddressStreet = addressStreetState[1];
  
  var addressSuiteState = useState('');
  var addressSuite = addressSuiteState[0];
  var setAddressSuite = addressSuiteState[1];
  
  var addressCityState = useState('');
  var addressCity = addressCityState[0];
  var setAddressCity = addressCityState[1];
  
  var addressStateState = useState('');
  var addressStateVal = addressStateState[0];
  var setAddressStateVal = addressStateState[1];
  
  var addressZipState = useState('');
  var addressZip = addressZipState[0];
  var setAddressZip = addressZipState[1];

  // Satellite offices state
  var satelliteOfficesState = useState([]);
  var satelliteOffices = satelliteOfficesState[0];
  var setSatelliteOffices = satelliteOfficesState[1];

  // Documents state
  var documentsState = useState([]);
  var documents = documentsState[0];
  var setDocuments = documentsState[1];
  
  var loadingDocsState = useState(false);
  var loadingDocs = loadingDocsState[0];
  var setLoadingDocs = loadingDocsState[1];
  
  var uploadingDocState = useState(false);
  var uploadingDoc = uploadingDocState[0];
  var setUploadingDoc = uploadingDocState[1];
  
  // Selected file (not yet uploaded)
  var selectedFileState = useState(null);
  var selectedFile = selectedFileState[0];
  var setSelectedFile = selectedFileState[1];
  
  var isDraggingState = useState(false);
  var isDragging = isDraggingState[0];
  var setIsDragging = isDraggingState[1];
  
  var docTypeState = useState('license');
  var docType = docTypeState[0];
  var setDocType = docTypeState[1];
  
  var docExpirationState = useState('');
  var docExpiration = docExpirationState[0];
  var setDocExpiration = docExpirationState[1];
  
  var docLicenseNumState = useState('');
  var docLicenseNum = docLicenseNumState[0];
  var setDocLicenseNum = docLicenseNumState[1];
  
  var docIssuingAuthState = useState('');
  var docIssuingAuth = docIssuingAuthState[0];
  var setDocIssuingAuth = docIssuingAuthState[1];
  
  // State certification upload state
  var stateCertStateState = useState('');
  var stateCertState = stateCertStateState[0];
  var setStateCertState = stateCertStateState[1];
  
  var stateCertCountyState = useState('');
  var stateCertCounty = stateCertCountyState[0];
  var setStateCertCounty = stateCertCountyState[1];
  
  // Attestation state for identity verification
  var attestationAcceptedState = useState(false);
  var attestationAccepted = attestationAcceptedState[0];
  var setAttestationAccepted = attestationAcceptedState[1];
  
  var stateRequirementsState = useState({});
  var stateRequirements = stateRequirementsState[0];
  var setStateRequirements = stateRequirementsState[1];
  
  var stateCertificationsState = useState({});
  var stateCertifications = stateCertificationsState[0];
  var setStateCertifications = stateCertificationsState[1];
  
  // Document viewing state
  var viewingDocState = useState(null);
  var viewingDoc = viewingDocState[0];
  var setViewingDoc = viewingDocState[1];

  
  // Notifications state
  var notificationsState = useState([]);
  var notifications = notificationsState[0];
  var setNotifications = notificationsState[1];
  
  var unreadCountState = useState(0);
  var unreadCount = unreadCountState[0];
  var setUnreadCount = unreadCountState[1];
  
  // Highlighted quote from URL (for email links)
  var highlightedQuoteState = useState(null);
  var highlightedQuote = highlightedQuoteState[0];
  var setHighlightedQuote = highlightedQuoteState[1];
  
  // Additional services state
  var additionalServicesState = useState([]);
  var additionalServices = additionalServicesState[0];
  var setAdditionalServices = additionalServicesState[1];
  
  var availableServicesState = useState({});
  var availableServices = availableServicesState[0];
  var setAvailableServices = availableServicesState[1];
  
  var savingServicesState = useState(false);
  var savingServices = savingServicesState[0];
  var setSavingServices = savingServicesState[1];
  
  // Local state for service edits (prices and toggles)
  var serviceEditsState = useState({});
  var serviceEdits = serviceEditsState[0];
  var setServiceEdits = serviceEditsState[1];


  useEffect(function() {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    var sessionId = searchParams.get('session_id');
    var success = searchParams.get('success');
    if (sessionId && success === 'true') {
      checkPayment(sessionId);
    }
    // Handle tab parameter from URL (e.g., from email links)
    var tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
    // Handle quote parameter - highlight specific quote
    var quoteParam = searchParams.get('quote');
    if (quoteParam) {
      setHighlightedQuote(quoteParam);
      // Clear the URL params after handling
      setTimeout(function() {
        navigate('/dashboard?tab=quotes', { replace: true });
      }, 100);
    }
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, authLoading]);

  function checkPayment(sessionId) {
    api.getCheckoutStatus(sessionId).then(function(response) {
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        loadData();
        navigate('/dashboard', { replace: true });
      }
    }).catch(function(err) {
      console.error('Payment check error:', err);
    });
  }

  function loadData() {
    setLoading(true);
    api.getMyProfile().then(function(profileRes) {
      var p = profileRes.data;
      setProfile(p);
      setDisplayName(p.display_name || '');
      setCompanyName(p.company_name || '');
      setPhone(p.phone || '');
      setEmail(p.email || '');
      setBio(p.bio || '');
      setWebsite(p.website || '');
      setYearsExp(p.years_experience || '');
      setResponseGuarantee(p.response_guarantee_hours || '');
      setPhotoUrl(p.photo_url || null);
      // Load pricing with toggles
      if (p.default_pricing) {
        setEnableRush(p.default_pricing.rush_fee != null);
        setRushFee(p.default_pricing.rush_fee || 35);
        setRushHours(p.default_pricing.rush_hours || 48);
        setEnableSameDay(p.default_pricing.same_day_fee != null);
        setSameDayFee(p.default_pricing.same_day_fee || 75);
        setEnableMileage(p.default_pricing.mileage_rate != null);
        setMileageRate(p.default_pricing.mileage_rate || 0.65);
        setFreeMiles(p.default_pricing.free_miles || 10);
        setEnableAdditionalAttempt(p.default_pricing.additional_attempt_fee != null);
        setAdditionalFee(p.default_pricing.additional_attempt_fee || 35);
      }
      // Load multi-serve discount
      if (p.multi_serve_discount) {
        setEnableMultiServe(p.multi_serve_discount.enabled);
        setDiscountType(p.multi_serve_discount.discount_type || 'percentage');
        setDiscountAmount(p.multi_serve_discount.discount_amount || 50);
      }
      // Load printing config
      if (p.printing_config) {
        setEnablePrinting(p.printing_config.enabled);
        setFreePages(p.printing_config.free_pages || 0);
        setPerPageFee(p.printing_config.per_page_fee || 0.25);
      }
      // Load server address
      if (p.server_address) {
        setAddressType(p.server_address.address_type || 'business');
        setAddressStreet(p.server_address.street || '');
        setAddressSuite(p.server_address.suite || '');
        setAddressCity(p.server_address.city || '');
        setAddressStateVal(p.server_address.state || '');
        setAddressZip(p.server_address.zip_code || '');
      }
      // Load satellite offices
      if (p.satellite_offices) {
        setSatelliteOffices(p.satellite_offices);
      }
      // Load additional services
      if (p.additional_services) {
        setAdditionalServices(p.additional_services);
      }
      // Load available services with profile data
      loadServicesWithProfile(p.additional_services || []);
      return api.getDashboard();
    }).then(function(analyticsRes) {
      setAnalytics(analyticsRes.data);
      return api.getReceivedQuotes();
    }).then(function(quotesRes) {
      setQuotes(quotesRes.data);
      setLoading(false);
      // Also load documents, notifications, state requirements, subscription limits
      loadDocuments();
      loadNotifications();
      loadStateRequirements();
      loadSubscriptionLimits();
      checkDowngradeStatus(); // Check if user needs to select coverage areas after cancellation
    }).catch(function(error) {
      if (error.response && error.response.status === 404) {
        setProfile(null);
      }
      setLoading(false);
    });
  }
  
  function loadStateRequirements() {
    api.getAllStateRequirements().then(function(res) {
      setStateRequirements(res.data);
    }).catch(function() {});
    
    api.getMyStateCertifications().then(function(res) {
      setStateCertifications(res.data);
    }).catch(function() {});
  }
  
  function loadServicesWithProfile(profileServices) {
    api.getAvailableServices().then(function(res) {
      setAvailableServices(res.data.services);
      // Initialize service edits from profile's additional_services
      var edits = {};
      Object.keys(res.data.services).forEach(function(serviceType) {
        var existing = profileServices.find(function(s) { return s.service_type === serviceType; });
        edits[serviceType] = {
          enabled: existing ? existing.enabled : false,
          price: existing ? existing.price : res.data.services[serviceType].default_price
        };
      });
      setServiceEdits(edits);
    }).catch(function() {});
  }
  
  function handleServiceToggle(serviceType, enabled) {
    setServiceEdits(function(prev) {
      return Object.assign({}, prev, {
        [serviceType]: Object.assign({}, prev[serviceType], { enabled: enabled })
      });
    });
  }
  
  function handleServicePriceChange(serviceType, price) {
    setServiceEdits(function(prev) {
      return Object.assign({}, prev, {
        [serviceType]: Object.assign({}, prev[serviceType], { price: price })
      });
    });
  }
  
  function handleSaveServices() {
    setSavingServices(true);
    // Build array of service updates
    var updates = Object.keys(serviceEdits).map(function(serviceType) {
      return {
        service_type: serviceType,
        enabled: serviceEdits[serviceType].enabled,
        price: serviceEdits[serviceType].price ? parseFloat(serviceEdits[serviceType].price) : null
      };
    });
    
    api.updateServices(updates).then(function(res) {
      toast.success('Services saved successfully!');
      setAdditionalServices(res.data.services);
      setSavingServices(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to save services');
      setSavingServices(false);
    });
  }
  
  function loadSubscriptionLimits() {
    api.getSubscriptionLimits().then(function(res) {
      setSubscriptionLimits(res.data);
    }).catch(function(err) {
      console.error('Failed to load subscription limits:', err);
    });
  }
  
  function checkDowngradeStatus() {
    api.getDowngradeStatus().then(function(res) {
      if (res.data.requires_action) {
        setDowngradeData(res.data);
        setShowDowngradeModal(true);
      }
    }).catch(function(err) {
      console.error('Failed to check downgrade status:', err);
    });
  }
  
  function toggleDowngradeAreaSelection(areaId) {
    if (selectedDowngradeAreas.includes(areaId)) {
      setSelectedDowngradeAreas(selectedDowngradeAreas.filter(function(id) { return id !== areaId; }));
    } else {
      if (selectedDowngradeAreas.length < 5) {
        setSelectedDowngradeAreas(selectedDowngradeAreas.concat([areaId]));
      } else {
        toast.error('You can only select up to 5 coverage areas');
      }
    }
  }
  
  function handleCompleteDowngrade() {
    if (selectedDowngradeAreas.length === 0) {
      toast.error('Please select at least one coverage area');
      return;
    }
    
    setProcessingDowngrade(true);
    api.completeDowngrade(selectedDowngradeAreas).then(function(res) {
      toast.success(res.data.message);
      setShowDowngradeModal(false);
      setDowngradeData(null);
      setSelectedDowngradeAreas([]);
      loadData(); // Reload profile data
      loadSubscriptionLimits();
      setProcessingDowngrade(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to complete downgrade');
      setProcessingDowngrade(false);
    });
  }
  
  function loadDocuments() {
    setLoadingDocs(true);
    api.getMyDocuments().then(function(res) {
      setDocuments(res.data);
      setLoadingDocs(false);
    }).catch(function() {
      setLoadingDocs(false);
    });
  }
  
  function loadNotifications() {
    api.getNotifications().then(function(res) {
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    }).catch(function() {});
  }
  
  function handleFileSelect(event) {
    var file = event.target.files ? event.target.files[0] : null;
    if (!file) return;
    
    // Validate file type
    var allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PDF, JPEG, PNG, or WebP');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }
    
    setSelectedFile(file);
    toast.success('File selected: ' + file.name);
  }
  
  function handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }
  
  function handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }
  
  function handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    
    var file = event.dataTransfer.files[0];
    if (file) {
      // Validate file type
      var allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Use PDF, JPEG, PNG, or WebP');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Maximum 5MB');
        return;
      }
      
      setSelectedFile(file);
      toast.success('File selected: ' + file.name);
    }
  }
  
  function handleDocumentUpload() {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    // Validate expiration date
    if (!docExpiration) {
      toast.error('Please enter an expiration date');
      return;
    }
    
    // Determine if this is a state certification upload
    var isStateCert = ['state_license', 'state_certification', 'state_registration', 'county_certification'].includes(docType);
    
    // For state certifications, validate state code
    if (isStateCert && !stateCertState) {
      toast.error('Please select a state for state certification documents');
      return;
    }
    
    // For FL county certifications, validate county
    if (docType === 'county_certification' && stateCertState === 'FL' && !stateCertCounty) {
      toast.error('Please enter the county for Florida county certifications');
      return;
    }
    
    // For identity verification, must accept attestation
    if (docType === 'identity_verification' && !attestationAccepted) {
      toast.error('Please accept the attestation to submit identity verification');
      return;
    }
    
    setUploadingDoc(true);
    api.uploadDocument(
      selectedFile, 
      docType, 
      docExpiration, 
      docLicenseNum || null, 
      docIssuingAuth || null, 
      null,
      isStateCert ? stateCertState : null,
      isStateCert && stateCertCounty ? stateCertCounty : null,
      docType === 'identity_verification' ? attestationAccepted : null
    )
      .then(function(res) {
        toast.success('Document uploaded! Pending admin verification.');
        loadDocuments();
        loadStateRequirements();
        // Reset form
        setSelectedFile(null);
        setDocExpiration('');
        setDocLicenseNum('');
        setDocIssuingAuth('');
        setStateCertState('');
        setStateCertCounty('');
        setAttestationAccepted(false);
        setUploadingDoc(false);
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Failed to upload document');
        setUploadingDoc(false);
      });
  }
  
  function handleDeleteDocument(docId) {
    api.deleteDocument(docId).then(function() {
      toast.success('Document deleted');
      loadDocuments();
      loadStateRequirements();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    });
  }
  
  function handleMarkNotificationRead(notificationId) {
    api.markNotificationRead(notificationId).then(function() {
      loadNotifications();
    });
  }
  
  function formatDate(dateStr) {
    if (!dateStr) return '-';
    // For date-only strings (YYYY-MM-DD), append T12:00:00 to avoid timezone issues
    // This ensures the date displays as the user entered it
    var dateToFormat = dateStr;
    if (dateStr.length === 10 && dateStr.includes('-')) {
      dateToFormat = dateStr + 'T12:00:00';
    }
    return new Date(dateToFormat).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  
  function getDocStatusBadge(status) {
    var colors = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      verified: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      expired: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return <Badge className={colors[status] + ' uppercase'}>{status}</Badge>;
  }
  
  function handlePhotoUpload(event) {
    var file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    var allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPEG, PNG, WebP, or GIF');
      return;
    }
    
    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum 2MB');
      return;
    }
    
    setUploadingPhoto(true);
    api.uploadPhoto(file).then(function(res) {
      setPhotoUrl(res.data.photo_url);
      toast.success('Photo uploaded successfully!');
      // Refresh user in auth context to update header photo
      if (auth.refreshUser) {
        auth.refreshUser();
      }
      setUploadingPhoto(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
      setUploadingPhoto(false);
    });
  }
  
  function handlePhotoDelete() {
    setUploadingPhoto(true);
    api.deletePhoto().then(function() {
      setPhotoUrl(null);
      toast.success('Photo deleted');
      // Refresh user in auth context to update header photo
      if (auth.refreshUser) {
        auth.refreshUser();
      }
      setUploadingPhoto(false);
    }).catch(function() {
      toast.error('Failed to delete photo');
      setUploadingPhoto(false);
    });
  }
  
  function createProfile(e) {
    e.preventDefault();
    setSaving(true);
    api.createProfile({
      display_name: displayName,
      company_name: companyName,
      phone: phone,
      email: email,
      bio: bio,
      website: website
    }).then(function() {
      toast.success('Profile created!');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to create profile');
      setSaving(false);
    });
  }

  function updateProfile() {
    setSaving(true);
    var data = {
      display_name: displayName,
      company_name: companyName,
      phone: phone,
      email: email,
      bio: bio,
      website: website,
      server_address: {
        address_type: addressType,
        street: addressStreet,
        suite: addressSuite,
        city: addressCity,
        state: addressStateVal,
        zip_code: addressZip,
        is_public: true
      },
      satellite_offices: satelliteOffices || []
    };
    if (yearsExp) data.years_experience = parseInt(yearsExp);
    if (responseGuarantee) data.response_guarantee_hours = parseInt(responseGuarantee);
    api.updateProfile(data).then(function() {
      toast.success('Profile updated!');
      loadData();
      // Refresh user in auth context to update header display name/photo
      if (auth.refreshUser) {
        auth.refreshUser();
      }
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update profile');
      setSaving(false);
    });
  }

  function updateDefaultPricing() {
    setSaving(true);
    api.updateProfile({
      default_pricing: {
        mileage_rate: enableMileage ? parseFloat(mileageRate) : null,
        free_miles: enableMileage ? parseInt(freeMiles) : null,
        additional_attempt_fee: enableAdditionalAttempt ? parseFloat(additionalFee) : null
      },
      multi_serve_discount: {
        enabled: enableMultiServe,
        discount_type: discountType,
        discount_amount: parseFloat(discountAmount) || 0,
        description: enableMultiServe ? (discountType === 'percentage' ? discountAmount + '% off additional papers at same address' : '$' + discountAmount + ' off additional papers at same address') : null
      },
      printing_config: {
        enabled: enablePrinting,
        free_pages: parseInt(freePages) || 0,
        per_page_fee: parseFloat(perPageFee) || 0.25
      }
    }).then(function() {
      toast.success('Pricing updated!');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update settings');
      setSaving(false);
    });
  }

  function addCoverage() {
    if (!coverageStateVal) {
      toast.error('Please select a state');
      return;
    }
    
    // Check freemium limits before adding
    if (subscriptionLimits && !subscriptionLimits.is_subscriber) {
      // Block state coverage for free users
      if (coverageType === 'state') {
        toast.error('Statewide coverage requires a subscription. Upgrade to unlock this feature!');
        return;
      }
      // Check area count limit
      if (!subscriptionLimits.limits.can_add_more_areas) {
        toast.error('You\'ve reached the free account limit of 5 coverage areas. Upgrade for unlimited coverage!');
        return;
      }
    }
    
    var newArea = {
      area_id: String(Date.now()),
      type: coverageType,
      state: coverageStateVal.toUpperCase(),
      attempts_included: parseInt(coverageAttempts) || 3,
      pricing_type: coveragePricingType
    };
    
    // Set pricing based on pricing type
    if (coveragePricingType === 'fixed') {
      newArea.base_fee = parseFloat(coverageFee) || 75;
    } else if (coveragePricingType === 'variable') {
      newArea.min_fee = parseFloat(coverageMinFee) || 50;
      newArea.max_fee = parseFloat(coverageMaxFee) || 150;
      newArea.base_fee = parseFloat(coverageMinFee) || 50; // Use min as base
    } else if (coveragePricingType === 'call_for_quote') {
      newArea.base_fee = 0;
    }
    
    // Set per-area rush and same day fees
    if (coverageRushEnabled) {
      newArea.rush_fee = parseFloat(coverageRushFee) || 35;
      newArea.rush_hours = parseInt(coverageRushHours) || 24;
    }
    if (coverageSameDayEnabled) {
      newArea.same_day_fee = parseFloat(coverageSameDayFee) || 75;
    }
    
    if (coverageType === 'city' && coverageCity) {
      newArea.city = coverageCity;
    } else if (coverageType === 'county' && coverageCounty) {
      newArea.county = coverageCounty;
    } else if (coverageType === 'zip' && coverageZipCodes) {
      newArea.zip_codes = coverageZipCodes.split(',').map(function(z) { return z.trim(); }).filter(function(z) { return z; });
    }
    // For state type, we just need state and base_fee (already set)
    var areas = profile.coverage_areas ? profile.coverage_areas.slice() : [];
    areas.push(newArea);
    // Sort alphabetically by state, then type, then county/zip
    areas.sort(function(a, b) {
      if (a.state !== b.state) return a.state.localeCompare(b.state);
      if (a.type !== b.type) {
        var typeOrder = { state: 0, county: 1, city: 2, zip: 3 };
        return (typeOrder[a.type] || 99) - (typeOrder[b.type] || 99);
      }
      var aName = a.county || a.city || (a.zip_codes ? a.zip_codes[0] : '') || '';
      var bName = b.county || b.city || (b.zip_codes ? b.zip_codes[0] : '') || '';
      return aName.localeCompare(bName);
    });
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      toast.success('Coverage area added!');
      setCoverageStateVal('');
      setCoverageCity('');
      setCoverageCounty('');
      setCoverageZipCodes('');
      setCoverageFee(75);
      setCoverageAttempts(3);
      setCoverageType('county');
      setCoveragePricingType('fixed');
      setCoverageMinFee(50);
      setCoverageMaxFee(150);
      setCoverageRushEnabled(false);
      setCoverageRushFee(35);
      setCoverageRushHours(24);
      setCoverageSameDayEnabled(false);
      setCoverageSameDayFee(75);
      loadData();
      loadSubscriptionLimits(); // Refresh limits after adding
      setSaving(false);
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to add coverage area');
      setSaving(false);
    });
  }

  function applyToAllAreas() {
    if (!profile.coverage_areas || profile.coverage_areas.length === 0) {
      toast.error('No coverage areas to update');
      return;
    }
    
    var areas = profile.coverage_areas.map(function(area) {
      var updated = Object.assign({}, area);
      if (applyAllRushEnabled) {
        updated.rush_fee = parseFloat(applyAllRushFee) || 35;
        updated.rush_hours = parseInt(applyAllRushHours) || 24;
      }
      if (applyAllSameDayEnabled) {
        updated.same_day_fee = parseFloat(applyAllSameDayFee) || 75;
      }
      return updated;
    });
    
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      var updates = [];
      if (applyAllRushEnabled) updates.push('Rush fees');
      if (applyAllSameDayEnabled) updates.push('Same Day fees');
      toast.success(updates.join(' and ') + ' applied to all areas!');
      setShowApplyAllModal(false);
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update areas');
      setSaving(false);
    });
  }

  function updateAreaFee(areaId, newFee) {
    var areas = profile.coverage_areas.map(function(a) {
      if (a.area_id === areaId) {
        return Object.assign({}, a, { base_fee: parseFloat(newFee) });
      }
      return a;
    });
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      toast.success('Fee updated!');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update fee');
      setSaving(false);
    });
  }
  
  function updateArea(areaId, updates) {
    var areas = profile.coverage_areas.map(function(a) {
      if (a.area_id === areaId) {
        return Object.assign({}, a, updates);
      }
      return a;
    });
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      toast.success('Coverage area updated!');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update coverage area');
      setSaving(false);
    });
  }

  function updateAreaZips(areaId, newZips) {
    var areas = profile.coverage_areas.map(function(a) {
      if (a.area_id === areaId) {
        return Object.assign({}, a, { zip_codes: newZips });
      }
      return a;
    });
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      toast.success('ZIP codes updated!');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to update ZIP codes');
      setSaving(false);
    });
  }

  function removeCoverage(areaId) {
    var areas = profile.coverage_areas.filter(function(a) { return a.area_id !== areaId; });
    setSaving(true);
    api.updateProfile({ coverage_areas: areas }).then(function() {
      toast.success('Coverage area removed');
      loadData();
      setSaving(false);
    }).catch(function() {
      toast.error('Failed to remove coverage area');
      setSaving(false);
    });
  }

  // Full state name lookup function
  function getFullStateName(code) {
    var names = {
      AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
      CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
      HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
      KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
      MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
      MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
      NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
      OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
      SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
      VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
      DC: 'Washington D.C.'
    };
    return names[code] || code;
  }
  
  function formatCoverageLabel(area) {
    var stateName = getFullStateName(area.state);
    if (area.type === 'state') {
      return stateName;
    }
    if (area.zip_codes && area.zip_codes.length > 0) {
      var zips = area.zip_codes.slice(0, 3).join(', ');
      var more = area.zip_codes.length > 3 ? ' +' + (area.zip_codes.length - 3) + ' more' : '';
      return 'ZIP: ' + zips + more + ' (' + stateName + ')';
    }
    if (area.county) {
      return area.county + ' County, ' + stateName;
    }
    if (area.city) {
      return area.city + ', ' + stateName;
    }
    return stateName;
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col noise-overlay">
        <Navbar />
        <main className="flex-1 py-12">
          <div className="section-container max-w-2xl">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-white">Set Up Your Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={createProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="create-display-name" className="text-slate-300">Display Name *</Label>
                    <Input id="create-display-name" name="display_name" required value={displayName} onChange={function(e) { setDisplayName(e.target.value); }} placeholder="John Smith" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="create-company-name" className="text-slate-300">Company Name</Label>
                    <Input id="create-company-name" name="company_name" value={companyName} onChange={function(e) { setCompanyName(e.target.value); }} placeholder="Smith Process Service" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="create-phone" className="text-slate-300">Phone *</Label>
                    <Input id="create-phone" name="phone" required value={phone} onChange={function(e) { setPhone(e.target.value); }} placeholder="(555) 123-4567" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="create-email" className="text-slate-300">Email *</Label>
                    <Input id="create-email" name="email" type="email" required value={email} onChange={function(e) { setEmail(e.target.value); }} placeholder="you@example.com" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <div>
                    <Label htmlFor="create-bio" className="text-slate-300">Bio</Label>
                    <Textarea id="create-bio" name="bio" value={bio} onChange={function(e) { setBio(e.target.value); }} placeholder="Tell clients about yourself..." className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                  </div>
                  <Button type="submit" className="w-full btn-accent" disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Create Profile
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  var trustScore = analytics && analytics.trust_score ? analytics.trust_score.overall : 50;
  var profileViews = analytics ? analytics.profile_views : 0;
  var quoteRequests = analytics ? analytics.quote_requests : 0;
  var reviewCount = analytics ? analytics.review_count : 0;
  var isActive = profile.subscription_status === 'active';
  var isListed = profile.listed;
  var isFoundingMember = profile.is_founding_member;
  var coverageAreas = profile.coverage_areas || [];
  
  // State name mapping for sorting
  var stateNames = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'Washington D.C.'
  };
  
  // Sort coverage areas A-Z: states first, then counties, then cities, then zips alphabetically
  var sortedCoverageAreas = coverageAreas.slice().sort(function(a, b) {
    // States come first, then counties, then cities, then zips
    var typeOrder = { state: 1, county: 2, city: 3, zip: 4 };
    var typeA = typeOrder[a.type] || 4;
    var typeB = typeOrder[b.type] || 4;
    if (typeA !== typeB) return typeA - typeB;
    
    // Same type - sort alphabetically
    var nameA = a.type === 'state' ? (stateNames[a.state] || a.state) : (a.county || a.city || '') + ', ' + (stateNames[a.state] || a.state);
    var nameB = b.type === 'state' ? (stateNames[b.state] || b.state) : (b.county || b.city || '') + ', ' + (stateNames[b.state] || b.state);
    return nameA.localeCompare(nameB);
  });
  
  // Group coverage areas by type for display
  var stateAreas = sortedCoverageAreas.filter(function(a) { return a.type === 'state'; });
  var countyAreas = sortedCoverageAreas.filter(function(a) { return a.type === 'county'; });
  var cityAreas = sortedCoverageAreas.filter(function(a) { return a.type === 'city'; });
  var zipAreas = sortedCoverageAreas.filter(function(a) { return a.type === 'zip'; });
  
  var coverageStats = analytics ? analytics.coverage_stats : { count: coverageAreas.length, breakdown: {} };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      {/* Downgrade Selection Modal */}
      <Dialog open={showDowngradeModal} onOpenChange={function() {}}>
        <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-800" onPointerDownOutside={function(e) { e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              Subscription Ended - Select Coverage Areas
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">
              Your subscription has ended. Free accounts are limited to <span className="text-amber-400 font-medium">5 coverage areas</span> (county, city, or ZIP code). Statewide coverage requires an active subscription.
            </p>
            
            <p className="text-slate-400 text-sm">
              Select up to 5 areas to keep active. Your remaining areas will be saved and restored if you resubscribe.
            </p>
            
            <div className="text-sm text-slate-500">
              Selected: <span className={selectedDowngradeAreas.length === 5 ? "text-amber-400" : "text-teal-400"}>{selectedDowngradeAreas.length}/5</span>
            </div>
            
            {downgradeData && downgradeData.stored_coverage_areas && (
              <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                {downgradeData.stored_coverage_areas.filter(function(area) { return area.type !== 'state'; }).map(function(area) {
                  var areaId = area.area_id || String(area.id);
                  var isSelected = selectedDowngradeAreas.includes(areaId);
                  var areaLabel = area.type === 'county' ? (area.county + ' County, ' + area.state) :
                                  area.type === 'city' ? (area.city + ', ' + area.state) :
                                  area.type === 'zip' ? (area.zip_codes ? area.zip_codes.join(', ') + ' (' + area.state + ')' : area.state) :
                                  area.state;
                  
                  return (
                    <div 
                      key={areaId}
                      onClick={function() { toggleDowngradeAreaSelection(areaId); }}
                      className={'p-3 rounded-lg border cursor-pointer transition-all ' + (isSelected ? 'bg-teal-500/20 border-teal-500/50' : 'bg-slate-800/50 border-slate-700 hover:border-slate-600')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={'w-5 h-5 rounded border flex items-center justify-center ' + (isSelected ? 'bg-teal-500 border-teal-500' : 'border-slate-600')}>
                            {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium capitalize">{area.type}</p>
                            <p className="text-slate-400 text-xs">{areaLabel}</p>
                          </div>
                        </div>
                        <span className="text-slate-500 text-xs">${area.base_fee || area.min_fee || 0}</span>
                      </div>
                    </div>
                  );
                })}
                
                {/* Show state coverage as disabled */}
                {downgradeData.stored_coverage_areas.filter(function(area) { return area.type === 'state'; }).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500 mb-2">Statewide coverage (requires subscription):</p>
                    {downgradeData.stored_coverage_areas.filter(function(area) { return area.type === 'state'; }).map(function(area) {
                      return (
                        <div key={area.area_id || area.state} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/50 opacity-60">
                          <div className="flex items-center gap-3">
                            <Shield className="h-4 w-4 text-slate-500" />
                            <p className="text-slate-500 text-sm">{area.state} Statewide</p>
                            <span className="ml-auto text-xs text-amber-500/70">PRO only</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={handleCompleteDowngrade}
                disabled={selectedDowngradeAreas.length === 0 || processingDowngrade}
                className="flex-1 btn-accent"
              >
                {processingDowngrade ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Selection
              </Button>
              <Button asChild variant="outline" className="border-teal-500/30 text-teal-400">
                <Link to="/pricing">Resubscribe</Link>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Navbar />
      <main className="flex-1 py-8 relative">
        <div className="absolute inset-x-0 top-0 h-72 pointer-events-none dark:hidden" style={{ background: 'radial-gradient(700px 240px at 50% 0%, hsl(226 100% 55% / 0.06), transparent 70%)' }} />
        <div className="absolute inset-x-0 top-0 h-72 pointer-events-none hidden dark:block" style={{ background: 'radial-gradient(800px 280px at 50% 0%, hsl(226 100% 55% / 0.10), transparent 70%)' }} />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] mb-2">◢ CONTROL ROOM</div>
              <h1 className="font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>
                Dashboard.
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-[hsl(220,12%,72%)]">Profile, coverage, quotes, invoices — all in one place.</p>
            </div>
            {!isActive && (
              <Button asChild className="btn-accent">
                <Link to="/pricing">{isListed ? "Upgrade to Pro" : "View Plans"}</Link>
              </Button>
            )}
          </div>

          {!isActive && (
            <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-400" />
              <p className="text-amber-400 text-sm">
                {isListed 
                  ? "Free Account: Your profile is live with up to 5 coverage areas." 
                  : coverageAreas.length === 0
                    ? "Complete your profile: Add coverage areas and verify your identity to appear in search."
                    : "Verify your identity or upload state certifications to appear in search."}
              </p>
              {!isListed && (
                <Button asChild size="sm" variant="outline" className="ml-auto border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  <Link to="/pricing">View Plans</Link>
                </Button>
              )}
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            {/* Desktop Tabs - Wrapped in scrollbar-hide container */}
            <div className="hidden md:block overflow-x-auto scrollbar-hide">
              <TabsList className="inline-flex w-full bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                <TabsTrigger value="overview" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><LayoutDashboard className="h-4 w-4 mr-1.5" />Overview</TabsTrigger>
                <TabsTrigger value="profile" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><User className="h-4 w-4 mr-1.5" />Profile</TabsTrigger>
                <TabsTrigger value="coverage" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><MapPin className="h-4 w-4 mr-1.5" />Coverage</TabsTrigger>
                <TabsTrigger value="services" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><Award className="h-4 w-4 mr-1.5" />Services</TabsTrigger>
                <TabsTrigger value="documents" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><FileText className="h-4 w-4 mr-1.5" />Docs</TabsTrigger>
                {profile?.subscription_status === 'active' ? (
                  <TabsTrigger value="routes" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><Route className="h-4 w-4 mr-1.5" />Routes</TabsTrigger>
                ) : (
                <button
                  type="button"
                  onClick={() => setShowSubRequired(true)}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium text-slate-500 opacity-60 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <Route className="h-4 w-4 mr-1.5" />Routes<Lock className="h-3 w-3 ml-1" />
                </button>
              )}
              {profile?.subscription_status === 'active' ? (
                <TabsTrigger value="invoices" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><Receipt className="h-4 w-4 mr-1.5" />Invoices</TabsTrigger>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSubRequired(true)}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium text-slate-500 opacity-60 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <Receipt className="h-4 w-4 mr-1.5" />Invoices<Lock className="h-3 w-3 ml-1" />
                </button>
              )}
              <TabsTrigger value="quotes" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><MessageSquare className="h-4 w-4 mr-1.5" />Quotes</TabsTrigger>
              {profile?.subscription_status === 'active' ? (
                <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><BarChart3 className="h-4 w-4 mr-1.5" />Analytics</TabsTrigger>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSubRequired(true)}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium text-slate-500 opacity-60 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <BarChart3 className="h-4 w-4 mr-1.5" />Analytics<Lock className="h-3 w-3 ml-1" />
                </button>
              )}
              {profile?.subscription_status === 'active' ? (
                <TabsTrigger value="mileage" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><Car className="h-4 w-4 mr-1.5" />Mileage</TabsTrigger>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSubRequired(true)}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium text-slate-500 opacity-60 hover:opacity-80 transition-opacity cursor-pointer"
                >
                  <Car className="h-4 w-4 mr-1.5" />Mileage<Lock className="h-3 w-3 ml-1" />
                </button>
              )}
              <TabsTrigger value="complaints" className="data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400 text-sm px-3 whitespace-nowrap"><Flag className="h-4 w-4 mr-1.5" />Reports</TabsTrigger>
              </TabsList>
            </div>
            
            {/* Mobile Tabs - Two rows for better fit */}
            <div className="md:hidden space-y-1">
              <TabsList className="grid grid-cols-6 gap-0.5 h-auto p-0.5 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                <TabsTrigger value="overview" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <LayoutDashboard className="h-3.5 w-3.5" />
                  <span>Home</span>
                </TabsTrigger>
                <TabsTrigger value="profile" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <User className="h-3.5 w-3.5" />
                  <span>Profile</span>
                </TabsTrigger>
                <TabsTrigger value="coverage" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>Areas</span>
                </TabsTrigger>
                <TabsTrigger value="services" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <Award className="h-3.5 w-3.5" />
                  <span>Services</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <FileText className="h-3.5 w-3.5" />
                  <span>Docs</span>
                </TabsTrigger>
                {profile?.subscription_status === 'active' ? (
                  <TabsTrigger value="routes" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                    <Route className="h-3.5 w-3.5" />
                    <span>Routes</span>
                  </TabsTrigger>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSubRequired(true)}
                    className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] text-slate-500 opacity-50"
                  >
                    <div className="relative">
                      <Route className="h-3.5 w-3.5" />
                      <Lock className="h-2 w-2 absolute -top-0.5 -right-1" />
                    </div>
                    <span>Routes</span>
                  </button>
                )}
              </TabsList>
              <TabsList className="grid grid-cols-5 gap-0.5 h-auto p-0.5 bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
                {profile?.subscription_status === 'active' ? (
                  <TabsTrigger value="invoices" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                    <Receipt className="h-3.5 w-3.5" />
                    <span>Bills</span>
                  </TabsTrigger>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSubRequired(true)}
                    className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] text-slate-500 opacity-50"
                  >
                    <div className="relative">
                      <Receipt className="h-3.5 w-3.5" />
                      <Lock className="h-2 w-2 absolute -top-0.5 -right-1" />
                    </div>
                    <span>Bills</span>
                  </button>
                )}
                <TabsTrigger value="quotes" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>Quotes</span>
                </TabsTrigger>
                {profile?.subscription_status === 'active' ? (
                  <TabsTrigger value="analytics" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                    <BarChart3 className="h-3.5 w-3.5" />
                    <span>Stats</span>
                  </TabsTrigger>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSubRequired(true)}
                    className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] text-slate-500 opacity-50"
                  >
                    <div className="relative">
                      <BarChart3 className="h-3.5 w-3.5" />
                      <Lock className="h-2 w-2 absolute -top-0.5 -right-1" />
                    </div>
                    <span>Stats</span>
                  </button>
                )}
                {profile?.subscription_status === 'active' ? (
                  <TabsTrigger value="mileage" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                    <Car className="h-3.5 w-3.5" />
                    <span>Miles</span>
                  </TabsTrigger>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowSubRequired(true)}
                    className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] text-slate-500 opacity-50"
                  >
                    <div className="relative">
                      <Car className="h-3.5 w-3.5" />
                      <Lock className="h-2 w-2 absolute -top-0.5 -right-1" />
                    </div>
                    <span>Miles</span>
                  </button>
                )}
                <TabsTrigger value="complaints" className="flex flex-col items-center gap-0.5 py-1.5 px-0.5 text-[9px] data-[state=active]:bg-slate-100 dark:data-[state=active]:bg-slate-800 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white text-slate-600 dark:text-slate-400">
                  <Flag className="h-3.5 w-3.5" />
                  <span>Reports</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <OverviewTab 
                trustScore={trustScore}
                profileViews={profileViews}
                quoteRequests={quoteRequests}
                reviewCount={reviewCount}
                analytics={analytics}
                isActive={isActive}
                isListed={isListed}
                isFoundingMember={isFoundingMember}
                coverageStats={coverageStats}
              />
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              <ProfileTab
                photoUrl={photoUrl}
                displayName={displayName}
                setDisplayName={setDisplayName}
                companyName={companyName}
                setCompanyName={setCompanyName}
                phone={phone}
                setPhone={setPhone}
                email={email}
                setEmail={setEmail}
                website={website}
                setWebsite={setWebsite}
                yearsExp={yearsExp}
                setYearsExp={setYearsExp}
                responseGuarantee={responseGuarantee}
                setResponseGuarantee={setResponseGuarantee}
                bio={bio}
                setBio={setBio}
                addressType={addressType}
                setAddressType={setAddressType}
                addressStreet={addressStreet}
                setAddressStreet={setAddressStreet}
                addressSuite={addressSuite}
                setAddressSuite={setAddressSuite}
                addressCity={addressCity}
                setAddressCity={setAddressCity}
                addressStateVal={addressStateVal}
                setAddressStateVal={setAddressStateVal}
                addressZip={addressZip}
                setAddressZip={setAddressZip}
                satelliteOffices={satelliteOffices}
                setSatelliteOffices={setSatelliteOffices}
                uploadingPhoto={uploadingPhoto}
                handlePhotoUpload={handlePhotoUpload}
                handlePhotoDelete={handlePhotoDelete}
                updateProfile={updateProfile}
                saving={saving}
              />
            </TabsContent>

            {/* Coverage & Pricing Tab */}
            <TabsContent value="coverage" className="space-y-6">
              {/* Coverage Map Preview */}
              {sortedCoverageAreas.length > 0 && (
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="text-base text-white flex items-center gap-2">
                      <MapPinned className="h-4 w-4 text-cyan-400" />
                      Coverage Map Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-400 mb-4">This is how your coverage areas appear on your public profile.</p>
                    <Suspense fallback={<div className="h-[250px] bg-slate-800/50 rounded-lg flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>}>
                      <CoverageMap 
                        coverageAreas={sortedCoverageAreas} 
                        serverAddress={profile?.server_address}
                        satelliteOffices={satelliteOffices}
                        height="250px" 
                      />
                    </Suspense>
                  </CardContent>
                </Card>
              )}
              
              {/* Coverage Areas List */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-teal-400" />
                    Coverage Areas & Base Fees
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-400 mb-4">Set your base fee for each area you cover. Additional fees (rush, etc.) are applied on top.</p>
                  
                  {sortedCoverageAreas.length > 0 ? (
                    <div className="space-y-6">
                      {/* Statewide Coverage */}
                      {stateAreas.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-violet-500/50"></span>
                            Statewide Coverage
                          </p>
                          <div className="space-y-2">
                            {stateAreas.map(function(area) {
                              return <CoverageAreaItem key={area.area_id} area={area} onUpdateArea={updateArea} onUpdateFee={updateAreaFee} onUpdateZips={updateAreaZips} onRemove={removeCoverage} formatLabel={formatCoverageLabel} />;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Counties */}
                      {countyAreas.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-cyan-500/50"></span>
                            Counties
                          </p>
                          <div className="space-y-2">
                            {countyAreas.map(function(area) {
                              return <CoverageAreaItem key={area.area_id} area={area} onUpdateArea={updateArea} onUpdateFee={updateAreaFee} onUpdateZips={updateAreaZips} onRemove={removeCoverage} formatLabel={formatCoverageLabel} />;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Cities */}
                      {cityAreas.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-emerald-500/50"></span>
                            Cities
                          </p>
                          <div className="space-y-2">
                            {cityAreas.map(function(area) {
                              return <CoverageAreaItem key={area.area_id} area={area} onUpdateArea={updateArea} onUpdateFee={updateAreaFee} onUpdateZips={updateAreaZips} onRemove={removeCoverage} formatLabel={formatCoverageLabel} />;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Zip Codes */}
                      {zipAreas.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-amber-500/50"></span>
                            Zip Codes
                          </p>
                          <div className="space-y-2">
                            {zipAreas.map(function(area) {
                              return <CoverageAreaItem key={area.area_id} area={area} onUpdateArea={updateArea} onUpdateFee={updateAreaFee} onUpdateZips={updateAreaZips} onRemove={removeCoverage} formatLabel={formatCoverageLabel} />;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 space-y-2">
                      <p className="text-sm text-slate-500">No coverage areas added yet</p>
                      <p className="text-xs text-slate-600">
                        Free accounts can add up to 5 counties, cities, or ZIP codes. 
                        You may need to verify your identity or upload state certification first.
                      </p>
                    </div>
                  )}

                  {/* Freemium Limits Banner - Only show when user has at least 1 coverage area */}
                  {subscriptionLimits && !subscriptionLimits.is_subscriber && subscriptionLimits.limits.current_area_count > 0 && (
                    <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-400" />
                          <span className="text-sm text-amber-300">
                            Free Account: {subscriptionLimits.limits.current_area_count} of {subscriptionLimits.limits.free_area_limit} coverage areas used
                          </span>
                        </div>
                        <Link to="/pricing" className="text-xs text-teal-400 hover:text-teal-300 font-medium">
                          Upgrade for unlimited →
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Add New Coverage Area */}
                  <div className="mt-6 pt-6 border-t border-slate-800">
                    <p className="text-sm font-medium text-white mb-4">Add New Coverage Area</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-slate-400 text-xs">Type</Label>
                          <Select 
                            value={coverageType} 
                            onValueChange={function(value) {
                              // Block "state" selection for non-subscribers
                              if (value === 'state' && subscriptionLimits && !subscriptionLimits.is_subscriber) {
                                toast.error('Statewide coverage requires a subscription. Upgrade to unlock this feature!');
                                return;
                              }
                              setCoverageType(value);
                            }}
                          >
                            <SelectTrigger className="bg-slate-900/60 border-slate-700 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="state" className="text-slate-300">
                                <span className="flex items-center gap-2">
                                  Entire State
                                  {subscriptionLimits && !subscriptionLimits.is_subscriber && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">PRO</span>
                                  )}
                                </span>
                              </SelectItem>
                              <SelectItem value="county" className="text-slate-300">County</SelectItem>
                              <SelectItem value="city" className="text-slate-300">City</SelectItem>
                              <SelectItem value="zip" className="text-slate-300">Zip Codes</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="coverage-state" className="text-slate-400 text-xs">State *</Label>
                          <Input id="coverage-state" name="coverage_state" placeholder="TX" maxLength={2} value={coverageStateVal} onChange={function(e) { setCoverageStateVal(e.target.value.toUpperCase()); }} className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                        </div>
                        {coverageType === 'county' && (
                          <div>
                            <Label htmlFor="coverage-county" className="text-slate-400 text-xs">County *</Label>
                            <Input id="coverage-county" name="coverage_county" placeholder="Lubbock" value={coverageCounty} onChange={function(e) { setCoverageCounty(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                          </div>
                        )}
                        {coverageType === 'city' && (
                          <div>
                            <Label htmlFor="coverage-city" className="text-slate-400 text-xs">City *</Label>
                            <Input id="coverage-city" name="coverage_city" placeholder="Houston" value={coverageCity} onChange={function(e) { setCoverageCity(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                          </div>
                        )}
                        {coverageType === 'zip' && (
                          <div className="col-span-2">
                            <Label htmlFor="coverage-zipcodes" className="text-slate-400 text-xs">Zip Codes (comma separated)</Label>
                            <Input id="coverage-zipcodes" name="coverage_zipcodes" placeholder="79401, 79402, 79403" value={coverageZipCodes} onChange={function(e) { setCoverageZipCodes(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Pricing Type Options */}
                      <div className="flex flex-wrap gap-2 items-center">
                        <Label className="text-slate-400 text-xs mr-2">Pricing:</Label>
                        <Button 
                          type="button"
                          size="sm" 
                          variant={coveragePricingType === 'fixed' ? 'default' : 'outline'}
                          onClick={function() { setCoveragePricingType('fixed'); }}
                          className={coveragePricingType === 'fixed' ? 'bg-teal-500 text-slate-900 h-8' : 'border-slate-600 text-slate-300 h-8'}
                        >
                          Fixed Rate
                        </Button>
                        {coverageType === 'state' && (
                          <Button 
                            type="button"
                            size="sm" 
                            variant={coveragePricingType === 'variable' ? 'default' : 'outline'}
                            onClick={function() { setCoveragePricingType('variable'); }}
                            className={coveragePricingType === 'variable' ? 'bg-teal-500 text-slate-900 h-8' : 'border-slate-600 text-slate-300 h-8'}
                          >
                            Price Range
                          </Button>
                        )}
                        <Button 
                          type="button"
                          size="sm" 
                          variant={coveragePricingType === 'call_for_quote' ? 'default' : 'outline'}
                          onClick={function() { setCoveragePricingType('call_for_quote'); }}
                          className={coveragePricingType === 'call_for_quote' ? 'bg-amber-500 text-slate-900 h-8' : 'border-slate-600 text-slate-300 h-8'}
                        >
                          Contact for Quote
                        </Button>
                      </div>
                      
                      {/* Fee inputs based on pricing type */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {coveragePricingType === 'fixed' && (
                          <div>
                            <Label htmlFor="coverage-base-fee" className="text-slate-400 text-xs">Base Fee ($)</Label>
                            <Input id="coverage-base-fee" name="coverage_base_fee" type="number" step="0.01" value={coverageFee} onChange={function(e) { setCoverageFee(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
                          </div>
                        )}
                        {coveragePricingType === 'variable' && (
                          <>
                            <div>
                              <Label htmlFor="coverage-min-fee" className="text-slate-400 text-xs">Min Fee ($)</Label>
                              <Input id="coverage-min-fee" name="coverage_min_fee" type="number" step="0.01" value={coverageMinFee} onChange={function(e) { setCoverageMinFee(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
                            </div>
                            <div>
                              <Label htmlFor="coverage-max-fee" className="text-slate-400 text-xs">Max Fee ($)</Label>
                              <Input id="coverage-max-fee" name="coverage_max_fee" type="number" step="0.01" value={coverageMaxFee} onChange={function(e) { setCoverageMaxFee(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" />
                            </div>
                          </>
                        )}
                        {coveragePricingType === 'call_for_quote' && (
                          <div className="col-span-2 flex items-center">
                            <p className="text-sm text-slate-400">Clients will see "Contact for Quote" for this area</p>
                          </div>
                        )}
                        <div>
                          <Label htmlFor="coverage-attempts" className="text-slate-400 text-xs">Attempts Included</Label>
                          <Select value={String(coverageAttempts)} onValueChange={function(v) { setCoverageAttempts(parseInt(v)); }}>
                            <SelectTrigger id="coverage-attempts" className="bg-slate-900/60 border-slate-700 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700">
                              <SelectItem value="1" className="text-slate-300">1 attempt</SelectItem>
                              <SelectItem value="2" className="text-slate-300">2 attempts</SelectItem>
                              <SelectItem value="3" className="text-slate-300">3 attempts</SelectItem>
                              <SelectItem value="4" className="text-slate-300">4 attempts</SelectItem>
                              <SelectItem value="5" className="text-slate-300">5 attempts</SelectItem>
                              <SelectItem value="6" className="text-slate-300">6 attempts</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Per-Area Rush & Same Day Fees */}
                      <div className="mt-4 pt-4 border-t border-slate-700">
                        <p className="text-xs text-slate-500 mb-3">Optional: Set rush/same-day fees for this area</p>
                        <div className="space-y-3">
                          {/* Rush Fee Toggle */}
                          <div className="flex items-center gap-3">
                            <Switch checked={coverageRushEnabled} onCheckedChange={setCoverageRushEnabled} />
                            <span className="text-sm text-slate-300">Rush Service</span>
                            {coverageRushEnabled && (
                              <div className="flex items-center gap-2 ml-auto">
                                <Select value={String(coverageRushHours)} onValueChange={function(v) { setCoverageRushHours(parseInt(v)); }}>
                                  <SelectTrigger className="w-20 h-8 bg-slate-800 border-slate-600 text-white text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-slate-900 border-slate-700">
                                    <SelectItem value="12" className="text-slate-300">12h</SelectItem>
                                    <SelectItem value="24" className="text-slate-300">24h</SelectItem>
                                    <SelectItem value="48" className="text-slate-300">48h</SelectItem>
                                    <SelectItem value="72" className="text-slate-300">72h</SelectItem>
                                  </SelectContent>
                                </Select>
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                                  <Input id="coverage-rush-fee" name="coverage_rush_fee" type="number" value={coverageRushFee} onChange={function(e) { setCoverageRushFee(e.target.value); }} className="w-20 h-8 pl-5 bg-slate-800 border-slate-600 text-white text-xs" />
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Same Day Toggle */}
                          <div className="flex items-center gap-3">
                            <Switch checked={coverageSameDayEnabled} onCheckedChange={setCoverageSameDayEnabled} />
                            <span className="text-sm text-slate-300">Same Day</span>
                            {coverageSameDayEnabled && (
                              <div className="relative ml-auto">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                                <Input id="coverage-same-day-fee" name="coverage_same_day_fee" type="number" value={coverageSameDayFee} onChange={function(e) { setCoverageSameDayFee(e.target.value); }} className="w-20 h-8 pl-5 bg-slate-800 border-slate-600 text-white text-xs" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button onClick={addCoverage} className="btn-secondary mt-4" disabled={saving}>
                        <Plus className="h-4 w-4 mr-2" />Add Coverage Area
                      </Button>
                    </div>
                  </div>
                  
                  {/* Apply to All Areas Button */}
                  {profile && profile.coverage_areas && profile.coverage_areas.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <Button variant="outline" onClick={function() { setShowApplyAllModal(true); }} className="w-full border-slate-600 text-slate-300 hover:bg-slate-800">
                        Apply Rush/Same Day to All Areas
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Apply to All Modal */}
              {showApplyAllModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                  <Card className="w-full max-w-md mx-4 card-base">
                    <CardHeader>
                      <CardTitle className="text-white">Apply to All Coverage Areas</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-slate-400">Select which fees to apply to all {profile.coverage_areas.length} coverage areas. Only checked options will be applied.</p>
                      
                      {/* Rush Fee */}
                      <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700">
                        <div className="flex items-center gap-3 mb-2">
                          <Switch checked={applyAllRushEnabled} onCheckedChange={setApplyAllRushEnabled} />
                          <span className="text-sm font-medium text-white">Rush Service</span>
                        </div>
                        {applyAllRushEnabled && (
                          <div className="flex items-center gap-3 mt-2 pl-10">
                            <Select value={String(applyAllRushHours)} onValueChange={function(v) { setApplyAllRushHours(parseInt(v)); }}>
                              <SelectTrigger id="apply-all-rush-hours" className="w-24 h-8 bg-slate-800 border-slate-600 text-white text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-slate-700">
                                <SelectItem value="12" className="text-slate-300">12 hours</SelectItem>
                                <SelectItem value="24" className="text-slate-300">24 hours</SelectItem>
                                <SelectItem value="48" className="text-slate-300">48 hours</SelectItem>
                                <SelectItem value="72" className="text-slate-300">72 hours</SelectItem>
                              </SelectContent>
                            </Select>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                              <Input id="apply-all-rush-fee" name="apply_all_rush_fee" type="number" value={applyAllRushFee} onChange={function(e) { setApplyAllRushFee(e.target.value); }} className="w-24 h-8 pl-7 bg-slate-800 border-slate-600 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Same Day Fee */}
                      <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700">
                        <div className="flex items-center gap-3 mb-2">
                          <Switch checked={applyAllSameDayEnabled} onCheckedChange={setApplyAllSameDayEnabled} />
                          <span className="text-sm font-medium text-white">Same Day Service</span>
                        </div>
                        {applyAllSameDayEnabled && (
                          <div className="flex items-center gap-3 mt-2 pl-10">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                              <Input id="apply-all-same-day-fee" name="apply_all_same_day_fee" type="number" value={applyAllSameDayFee} onChange={function(e) { setApplyAllSameDayFee(e.target.value); }} className="w-24 h-8 pl-7 bg-slate-800 border-slate-600 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <Button variant="outline" onClick={function() { setShowApplyAllModal(false); }} className="flex-1 border-slate-600 text-slate-300">Cancel</Button>
                        <Button onClick={applyToAllAreas} disabled={saving || (!applyAllRushEnabled && !applyAllSameDayEnabled)} className="flex-1 btn-primary">
                          {saving ? 'Applying...' : 'Apply to All'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Additional Fees */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-400" />
                    Additional Fees
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-400 mb-6">Configure optional fees. Rush and Same Day fees are now set per coverage area above.</p>
                  
                  <div className="space-y-6">
                    {/* Mileage - Optional */}
                    <div className={'p-4 rounded-lg border ' + (enableMileage ? 'bg-slate-800/40 border-slate-700/50' : 'bg-slate-900/20 border-slate-800/30')}>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-white font-medium">Mileage Fee</Label>
                        <Switch checked={enableMileage} onCheckedChange={setEnableMileage} />
                      </div>
                      {enableMileage && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="mileage-rate" className="text-slate-400 text-xs">Rate per Mile ($)</Label>
                            <div className="relative mt-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                              <Input id="mileage-rate" name="mileage_rate" type="number" step="0.01" value={mileageRate} onChange={function(e) { setMileageRate(e.target.value); }} className="pl-7 bg-slate-900/60 border-slate-700 text-white" />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="free-miles" className="text-slate-400 text-xs">Free Miles Included</Label>
                            <Input id="free-miles" name="free_miles" type="number" value={freeMiles} onChange={function(e) { setFreeMiles(e.target.value); }} className="mt-1 bg-slate-900/60 border-slate-700 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Attempt Fee - Now toggleable */}
                    <div className={'p-4 rounded-lg border ' + (enableAdditionalAttempt ? 'bg-slate-800/40 border-slate-700/50' : 'bg-slate-900/20 border-slate-800/30')}>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-white font-medium">Additional Attempt Fee</Label>
                        <Switch checked={enableAdditionalAttempt} onCheckedChange={setEnableAdditionalAttempt} />
                      </div>
                      {enableAdditionalAttempt && (
                        <div>
                          <Label htmlFor="additional-attempt-fee" className="text-slate-400 text-xs">Fee per Additional Attempt ($)</Label>
                          <div className="relative mt-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                            <Input id="additional-attempt-fee" name="additional_attempt_fee" type="number" step="0.01" value={additionalFee} onChange={function(e) { setAdditionalFee(e.target.value); }} className="pl-7 bg-slate-900/60 border-slate-700 text-white" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Printing Fees */}
                    <div className={'p-4 rounded-lg border ' + (enablePrinting ? 'bg-slate-800/40 border-slate-700/50' : 'bg-slate-900/20 border-slate-800/30')}>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-white font-medium">Printing/Copies</Label>
                        <Switch checked={enablePrinting} onCheckedChange={setEnablePrinting} />
                      </div>
                      {enablePrinting && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="free-pages" className="text-slate-400 text-xs">Free Pages Included</Label>
                            <Input id="free-pages" name="free_pages" type="number" value={freePages} onChange={function(e) { setFreePages(e.target.value); }} className="mt-1 bg-slate-900/60 border-slate-700 text-white" />
                          </div>
                          <div>
                            <Label htmlFor="per-page-fee" className="text-slate-400 text-xs">Per Page Fee ($)</Label>
                            <div className="relative mt-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                              <Input id="per-page-fee" name="per_page_fee" type="number" step="0.01" value={perPageFee} onChange={function(e) { setPerPageFee(e.target.value); }} className="pl-7 bg-slate-900/60 border-slate-700 text-white" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Multi-Serve Discount */}
                    <div className={'p-4 rounded-lg border ' + (enableMultiServe ? 'bg-teal-500/5 border-teal-500/30' : 'bg-slate-900/20 border-slate-800/30')}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <Label className="text-white font-medium">Multi-Serve Discount</Label>
                          <p className="text-xs text-slate-500">Discount for serving multiple people at the same address</p>
                        </div>
                        <Switch checked={enableMultiServe} onCheckedChange={setEnableMultiServe} />
                      </div>
                      {enableMultiServe && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="discount-type" className="text-slate-400 text-xs">Discount Type</Label>
                            <Select value={discountType} onValueChange={setDiscountType}>
                              <SelectTrigger id="discount-type" className="mt-1 bg-slate-900/60 border-slate-700 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage (%)</SelectItem>
                                <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="discount-amount" className="text-slate-400 text-xs">{discountType === 'percentage' ? 'Discount %' : 'Discount $'}</Label>
                            <div className="relative mt-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{discountType === 'percentage' ? '%' : '$'}</span>
                              <Input id="discount-amount" name="discount_amount" type="number" step={discountType === 'percentage' ? '1' : '0.01'} value={discountAmount} onChange={function(e) { setDiscountAmount(e.target.value); }} className="pl-7 bg-slate-900/60 border-slate-700 text-white" />
                            </div>
                          </div>
                        </div>
                      )}
                      {enableMultiServe && (
                        <p className="text-xs text-teal-400 mt-3">
                          Preview: {discountType === 'percentage' ? discountAmount + '% off' : '$' + discountAmount + ' off'} additional papers at same address
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <Button onClick={updateDefaultPricing} className="mt-6 btn-accent" disabled={saving}>
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Pricing Settings
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quotes Tab */}
            <TabsContent value="quotes" className="space-y-6">
              <QuotesTab 
                quotes={quotes} 
                onSuccess={loadData} 
                highlightedQuote={highlightedQuote} 
                onClearHighlight={function() { setHighlightedQuote(null); }} 
              />
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-base text-white">Additional Services</CardTitle>
                  <p className="text-sm text-slate-400">Toggle services you offer beyond process serving and set your prices</p>
                </CardHeader>
                <CardContent>
                  {Object.keys(availableServices).length === 0 ? (
                    <div className="text-center py-8">
                      <Loader2 className="h-8 w-8 mx-auto animate-spin text-slate-500" />
                      <p className="text-slate-400 mt-2">Loading services...</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(availableServices).map(function(entry) {
                        var serviceType = entry[0];
                        var serviceInfo = entry[1];
                        var edits = serviceEdits[serviceType] || { enabled: false, price: serviceInfo.default_price };
                        var isEnabled = edits.enabled;
                        var needsVerification = serviceInfo.requires_verification;
                        
                        // Get existing service data for verification status
                        var existingService = additionalServices.find(function(s) { return s.service_type === serviceType; });
                        var verificationStatus = existingService ? existingService.verification_status : 'not_required';
                        var isVerified = verificationStatus === 'verified';
                        var isPending = verificationStatus === 'pending';
                        var isRejected = verificationStatus === 'rejected';
                        
                        return (
                          <div key={serviceType} className={'p-4 rounded-lg border transition-colors ' + (isEnabled ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-900/20 border-slate-800/30')}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={'w-10 h-10 rounded-lg flex items-center justify-center ' + (isEnabled ? (needsVerification && isVerified ? 'bg-emerald-500/10' : needsVerification ? 'bg-amber-500/10' : 'bg-teal-500/10') : 'bg-slate-800')}>
                                  <Award className={'h-5 w-5 ' + (isEnabled ? (needsVerification && isVerified ? 'text-emerald-400' : needsVerification ? 'text-amber-400' : 'text-teal-400') : 'text-slate-600')} />
                                </div>
                                <div>
                                  <p className={'font-medium ' + (isEnabled ? 'text-white' : 'text-slate-500')}>{serviceInfo.name}</p>
                                  {needsVerification && isEnabled && (
                                    <p className="text-xs text-slate-400">
                                      {isVerified ? 'License verified' : isPending ? 'Awaiting verification - Pending admin review' : isRejected ? 'Verification rejected' : (
                                        <button 
                                          type="button"
                                          onClick={function() { setActiveTab('documents'); }}
                                          className="text-teal-400 hover:text-teal-300 underline"
                                        >
                                          Upload {serviceInfo.name} license in Documents tab →
                                        </button>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                {needsVerification && isEnabled && (
                                  <Badge className={isVerified ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : isPending ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : isRejected ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}>
                                    {isVerified ? 'VERIFIED' : isPending ? 'PENDING' : isRejected ? 'REJECTED' : 'UNVERIFIED'}
                                  </Badge>
                                )}
                                <Switch 
                                  checked={isEnabled} 
                                  onCheckedChange={function(checked) { handleServiceToggle(serviceType, checked); }}
                                />
                              </div>
                            </div>
                            
                            {isEnabled && (
                              <div className="mt-4 pt-4 border-t border-slate-700/50">
                                <div className="flex items-center gap-4">
                                  <Label htmlFor={'service-price-' + serviceType} className="text-slate-400 text-sm whitespace-nowrap">Your Price:</Label>
                                  <div className="relative w-32">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                                    <Input 
                                      id={'service-price-' + serviceType}
                                      name="service_price"
                                      type="number" 
                                      step="0.01" 
                                      min="0"
                                      value={edits.price || ''} 
                                      onChange={function(e) { handleServicePriceChange(serviceType, e.target.value); }}
                                      placeholder={String(serviceInfo.default_price)}
                                      className="pl-7 bg-slate-900/60 border-slate-700 text-white h-9"
                                    />
                                  </div>
                                  <span className="text-xs text-slate-500">(Default: ${serviceInfo.default_price})</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Save Button */}
                  <div className="mt-6 pt-6 border-t border-slate-800">
                    <Button onClick={handleSaveServices} className="btn-accent" disabled={savingServices}>
                      {savingServices && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Services
                    </Button>
                  </div>
                  
                  {/* Services Info */}
                  <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                    <p className="text-sm font-medium text-white mb-2">Service Verification</p>
                    <p className="text-xs text-slate-400">
                      Services like Notary, Mobile Notary, and Private Investigator require verification. 
                      After enabling these services, upload your license/certification in the Documents tab.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-6">
              {/* Notifications Banner */}
              {unreadCount > 0 && (
                <Card className="card-base border-amber-500/30 bg-amber-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bell className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="text-sm font-medium text-white">You have {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}</p>
                          <p className="text-xs text-slate-400">Click to view your verification updates</p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        onClick={function() { 
                          notifications.forEach(function(n) { 
                            if (!n.read) handleMarkNotificationRead(n.id); 
                          }); 
                        }}
                      >
                        Mark All Read
                      </Button>
                    </div>
                    {notifications.filter(function(n) { return !n.read; }).slice(0, 3).map(function(n) {
                      return (
                        <div key={n.id} className="mt-3 p-3 bg-slate-800/60 rounded-lg border border-slate-700 cursor-pointer hover:border-slate-600" onClick={function() { handleMarkNotificationRead(n.id); }}>
                          <p className="text-sm text-white">{n.title}</p>
                          <p className="text-xs text-slate-400">{n.message}</p>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* State Certifications Info */}
              <Card className="card-base border-violet-500/30 bg-violet-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Shield className="h-5 w-5 text-violet-400" />
                    State Licensing Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-400 mb-4">
                    Some states require certification to serve process. Upload your state certification to add coverage areas in regulated states.
                  </p>
                  <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
                    {['AK', 'AZ', 'CA', 'FL', 'GA', 'KY', 'MO', 'MT', 'NV', 'NY', 'OK', 'TX', 'WA'].map(function(st) {
                      var req = stateRequirements.requirements ? stateRequirements.requirements[st] : null;
                      var certs = stateCertifications.certifications ? stateCertifications.certifications[st] : null;
                      var verifiedCert = certs && certs.find(function(c) { return c.status === 'verified'; });
                      var hasVerified = !!verifiedCert;
                      var hasPending = certs && certs.some(function(c) { return c.status === 'pending'; });
                      
                      // Check if expiring soon (< 60 days)
                      // Day calculation: expiration day counts as 1 day remaining (still valid until midnight)
                      var isExpiringSoon = false;
                      var isExpired = false;
                      var daysUntilExpiry = null;
                      if (verifiedCert && verifiedCert.expiration_date) {
                        var expDate = new Date(verifiedCert.expiration_date + 'T23:59:59');
                        var now = new Date();
                        var diffMs = expDate - now;
                        // If diff is positive, cert is still valid
                        if (diffMs > 0) {
                          // Add 1 because expiration day itself counts as a valid day
                          daysUntilExpiry = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                          isExpiringSoon = daysUntilExpiry <= 60;
                        } else {
                          isExpired = true;
                          daysUntilExpiry = 0;
                        }
                      }
                      
                      var daysLabel = daysUntilExpiry === 1 ? '1 Day' : daysUntilExpiry + ' Days';
                      var borderColor = isExpired ? 'border-red-500/50' : isExpiringSoon ? 'border-amber-500/50' : hasVerified ? 'border-emerald-500/30' : hasPending ? 'border-amber-500/30' : 'border-slate-700';
                      var bgColor = isExpired ? 'bg-red-500/10' : isExpiringSoon ? 'bg-amber-500/10' : hasVerified ? 'bg-emerald-500/10' : hasPending ? 'bg-amber-500/10' : 'bg-slate-800/40';
                      
                      return (
                        <div key={st} className={'p-2 rounded-lg border text-center text-xs ' + bgColor + ' ' + borderColor}>
                          <span className={'font-bold ' + (isExpired ? 'text-red-400' : isExpiringSoon ? 'text-amber-400' : hasVerified ? 'text-emerald-400' : hasPending ? 'text-amber-400' : 'text-slate-400')}>{st}</span>
                          <span className="block text-slate-500 text-[10px]">{req ? req.label : 'Required'}</span>
                          {isExpired && <span className="text-red-400 text-[10px] flex items-center justify-center gap-1"><AlertTriangle className="h-3 w-3" /> Expired</span>}
                          {isExpiringSoon && !isExpired && <span className="text-amber-400 text-[10px] flex items-center justify-center gap-1"><AlertTriangle className="h-3 w-3" /> {daysLabel}</span>}
                          {hasVerified && !isExpiringSoon && !isExpired && <span className="text-emerald-400 text-[10px]">✓ Verified</span>}
                          {hasPending && !hasVerified && <span className="text-amber-400 text-[10px]">Pending</span>}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Upload Document Section */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Upload className="h-5 w-5 text-teal-400" />
                    Upload Verification Document
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Document Type</Label>
                        <Select value={docType} onValueChange={function(v) { setDocType(v); setStateCertState(''); setStateCertCounty(''); }}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="identity_verification">Identity Verification (Government ID)</SelectItem>
                            <SelectItem value="insurance">Professional Liability Insurance</SelectItem>
                            <SelectItem value="bond">Surety Bond</SelectItem>
                            <SelectItem value="notary">Notary Commission</SelectItem>
                            <SelectItem value="private_investigator">Private Investigator License</SelectItem>
                            <SelectItem value="state_certification">State Certification (TX, AZ)</SelectItem>
                            <SelectItem value="state_registration">State Registration (CA, WA, MT)</SelectItem>
                            <SelectItem value="state_license">State License (AK, NV, OK)</SelectItem>
                            <SelectItem value="county_certification">County Certification (FL)</SelectItem>
                          </SelectContent>
                        </Select>
                        {docType === 'identity_verification' && (
                          <p className="text-xs text-amber-400 mt-1">For servers in states without licensing requirements. Upload a government-issued photo ID.</p>
                        )}
                        {docType === 'notary' && (
                          <p className="text-xs text-slate-400 mt-1">Upload your state-issued Notary Public commission. This validates both Notary Public and Mobile Notary services.</p>
                        )}
                        {docType === 'private_investigator' && (
                          <p className="text-xs text-slate-400 mt-1">Upload your state-issued Private Investigator license.</p>
                        )}
                      </div>
                      
                      {/* State selection for state certifications */}
                      {['state_certification', 'state_registration', 'state_license', 'county_certification'].includes(docType) && (
                        <div>
                          <Label className="text-slate-300">State *</Label>
                          <Select value={stateCertState} onValueChange={setStateCertState}>
                            <SelectTrigger className="mt-1 bg-slate-800 border-slate-700">
                              <SelectValue placeholder="Select state..." />
                            </SelectTrigger>
                            <SelectContent>
                              {docType === 'state_certification' && (
                                <>
                                  <SelectItem value="TX">Texas (JBCC Certification)</SelectItem>
                                  <SelectItem value="AZ">Arizona (Superior Court)</SelectItem>
                                  <SelectItem value="GA">Georgia (Sheriffs' Association)</SelectItem>
                                </>
                              )}
                              {docType === 'state_registration' && (
                                <>
                                  <SelectItem value="CA">California</SelectItem>
                                  <SelectItem value="WA">Washington</SelectItem>
                                  <SelectItem value="MT">Montana</SelectItem>
                                  <SelectItem value="KY">Kentucky (Special Bailiff)</SelectItem>
                                </>
                              )}
                              {docType === 'state_license' && (
                                <>
                                  <SelectItem value="AK">Alaska</SelectItem>
                                  <SelectItem value="NV">Nevada (PILB)</SelectItem>
                                  <SelectItem value="OK">Oklahoma</SelectItem>
                                  <SelectItem value="NY">New York City (DCWP)</SelectItem>
                                  <SelectItem value="MO">Missouri (St. Louis City)</SelectItem>
                                </>
                              )}
                              {docType === 'county_certification' && (
                                <SelectItem value="FL">Florida</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          {stateCertState && stateRequirements.requirements && stateRequirements.requirements[stateCertState] && (
                            <p className="text-xs text-slate-500 mt-1">{stateRequirements.requirements[stateCertState].description}</p>
                          )}
                        </div>
                      )}
                      
                      {/* County for FL */}
                      {docType === 'county_certification' && stateCertState === 'FL' && (
                        <div>
                          <Label htmlFor="doc-county" className="text-slate-300">County *</Label>
                          <Input 
                            id="doc-county"
                            name="doc_county"
                            value={stateCertCounty} 
                            onChange={function(e) { setStateCertCounty(e.target.value); }}
                            placeholder="e.g., Miami-Dade, Hillsborough"
                            className="mt-1 bg-slate-800 border-slate-700 text-white"
                          />
                          <p className="text-xs text-amber-400 mt-1">Florida certifications are county-specific. You must have separate certification for each county.</p>
                        </div>
                      )}
                      
                      <div>
                        <Label htmlFor="doc-expiration" className="text-slate-300">Expiration Date *</Label>
                        <Input 
                          id="doc-expiration"
                          name="doc_expiration"
                          type="date" 
                          value={docExpiration} 
                          onChange={function(e) { setDocExpiration(e.target.value); }}
                          className="mt-1 bg-slate-800 border-slate-700 text-white"
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      {(docType === 'license' || ['state_certification', 'state_registration', 'state_license', 'county_certification'].includes(docType)) && (
                        <>
                          <div>
                            <Label htmlFor="doc-license-num" className="text-slate-300">{docType === 'state_certification' && stateCertState === 'TX' ? 'JBCC Certification Number' : 'License/Certification Number'}</Label>
                            <Input 
                              id="doc-license-num"
                              name="doc_license_num"
                              value={docLicenseNum} 
                              onChange={function(e) { setDocLicenseNum(e.target.value); }}
                              placeholder={docType === 'state_certification' && stateCertState === 'TX' ? 'e.g., PSC-12345' : 'e.g., PS-12345'}
                              className="mt-1 bg-slate-800 border-slate-700 text-white"
                            />
                          </div>
                          <div>
                            <Label htmlFor="doc-issuing-auth" className="text-slate-300">Issuing Authority</Label>
                            <Input 
                              id="doc-issuing-auth"
                              name="doc_issuing_auth"
                              value={docIssuingAuth} 
                              onChange={function(e) { setDocIssuingAuth(e.target.value); }}
                              placeholder={stateCertState === 'TX' ? 'Texas JBCC' : stateCertState === 'CA' ? 'County Clerk' : 'Issuing authority'}
                              className="mt-1 bg-slate-800 border-slate-700 text-white"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-6">
                    <Label className="text-slate-300 block mb-2">Select Document (PDF, JPEG, PNG - Max 5MB)</Label>
                    <div 
                      className={'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ' + (isDragging ? 'border-teal-400 bg-teal-500/10' : selectedFile ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-700 hover:border-teal-500/50')}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={function() { document.getElementById('doc-file-input').click(); }}
                    >
                      {selectedFile ? (
                        <>
                          <CheckCircle className="h-8 w-8 mx-auto text-emerald-400 mb-2" />
                          <p className="text-sm text-emerald-400 font-medium">{selectedFile.name}</p>
                          <p className="text-xs text-slate-400 mt-1">Click or drop to replace</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 mx-auto text-slate-500 mb-2" />
                          <p className="text-sm text-slate-400">{isDragging ? 'Drop file here...' : 'Click to select file or drag and drop'}</p>
                        </>
                      )}
                      {['state_certification', 'state_registration', 'state_license', 'county_certification'].includes(docType) && stateCertState && (
                        <p className="text-xs text-teal-400 mt-2">Uploading {stateCertState} {docType.replace('_', ' ')}{stateCertCounty ? ` - ${stateCertCounty} County` : ''}</p>
                      )}
                      <input 
                        id="doc-file-input"
                        type="file" 
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={handleFileSelect}
                        className="hidden"
                        disabled={uploadingDoc}
                      />
                    </div>
                    
                    {/* Attestation for identity verification */}
                    {docType === 'identity_verification' && (
                      <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-amber-500/30">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={attestationAccepted}
                            onChange={function(e) { setAttestationAccepted(e.target.checked); }}
                            className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500"
                          />
                          <span className="text-sm text-slate-300">
                            <span className="font-medium text-amber-400">Attestation Required:</span> I certify that I am actively engaged in process serving and that all information provided is accurate. I understand falsifying this information will result in removal from Servio and forfeiture of any fees paid.
                          </span>
                        </label>
                      </div>
                    )}
                    
                    {/* Upload Button */}
                    <div className="mt-4 flex items-center gap-3">
                      <Button 
                        onClick={handleDocumentUpload}
                        disabled={!selectedFile || !docExpiration || uploadingDoc || (docType === 'identity_verification' && !attestationAccepted)}
                        className="btn-accent"
                      >
                        {uploadingDoc ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Document
                          </>
                        )}
                      </Button>
                      {selectedFile && (
                        <Button 
                          variant="outline" 
                          onClick={function() { setSelectedFile(null); }}
                          className="border-slate-700 text-slate-400"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Your Documents */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-base text-white">Your Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
                    </div>
                  ) : documents.filter(function(d) { return d.status !== 'rejected'; }).length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto text-slate-600 mb-3" />
                      <p className="text-slate-400">No documents uploaded yet</p>
                      <p className="text-xs text-slate-500 mt-1">Upload your license, insurance, bond, or state certifications for verification</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {documents.filter(function(d) { return d.status !== 'rejected'; }).map(function(doc) {
                        var isExpiringSoon = doc.status === 'verified' && new Date(doc.expiration_date) < new Date(Date.now() + 60 * 24 * 60 * 60 * 1000);
                        var isStateCert = ['state_license', 'state_certification', 'state_registration', 'county_certification', 'city_license'].includes(doc.document_type);
                        var isIdentityDoc = doc.document_type === 'identity_verification';
                        var docLabel = isStateCert && doc.state_code 
                          ? (doc.county ? doc.state_code + ' ' + doc.county + ' County' : doc.state_code + ' ' + doc.document_type.replace('_', ' '))
                          : isIdentityDoc ? 'Identity Verification (Government ID)'
                          : doc.document_type.replace('_', ' ');
                        return (
                          <div key={doc.id} className={'flex items-center justify-between p-4 rounded-lg border ' + (isExpiringSoon ? 'bg-amber-500/5 border-amber-500/30' : isStateCert ? 'bg-violet-500/5 border-violet-500/30' : isIdentityDoc ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-800/40 border-slate-700')}>
                            <div className="flex items-center gap-4">
                              <div className={'w-10 h-10 rounded-lg flex items-center justify-center ' + (isStateCert ? 'bg-violet-500/10' : isIdentityDoc ? 'bg-emerald-500/10' : doc.document_type === 'license' ? 'bg-teal-500/10' : doc.document_type === 'insurance' ? 'bg-cyan-500/10' : 'bg-purple-500/10')}>
                                {isStateCert ? <Shield className="h-5 w-5 text-violet-400" /> : isIdentityDoc ? <UserCheck className="h-5 w-5 text-emerald-400" /> : doc.document_type === 'license' ? <FileText className="h-5 w-5 text-teal-400" /> : doc.document_type === 'insurance' ? <Shield className="h-5 w-5 text-cyan-400" /> : <Award className="h-5 w-5 text-purple-400" />}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-white capitalize">{docLabel}</p>
                                  {isStateCert && doc.state_code && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 font-medium">{doc.state_code}</span>
                                  )}
                                  {isIdentityDoc && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-medium">ID</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-400">
                                  {(doc.license_number || doc.certification_number) && <span>#{doc.certification_number || doc.license_number}</span>}
                                  <span>Expires: {formatDate(doc.expiration_date)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {isExpiringSoon && doc.status === 'verified' && (
                                <div className="flex items-center gap-1 text-amber-400 text-xs">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span>Expiring soon</span>
                                </div>
                              )}
                              {getDocStatusBadge(doc.status)}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={function() { 
                                  api.getDocument(doc.id).then(function(res) {
                                    setViewingDoc(res.data);
                                  }).catch(function() {
                                    toast.error('Failed to load document');
                                  });
                                }}
                                className="text-slate-400 hover:text-teal-400"
                                title="View document"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {doc.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={function() { handleDeleteDocument(doc.id); }}
                                  className="text-slate-400 hover:text-red-400"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Expiration Reminders Info */}
              <Card className="card-base">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Bell className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white">Automatic Expiration Reminders</p>
                      <p className="text-xs text-slate-400 mt-1">
                        You will receive notifications at 60, 30, and 7 days before your documents expire. 
                        Make sure to upload renewed documents before expiration to maintain your verified status.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Routes Tab - Subscriber Only */}
            {profile?.subscription_status === 'active' && (
              <TabsContent value="routes" className="space-y-6">
                {Capacitor.isNativePlatform() ? (
                  <MobileRoutesPage profile={profile} />
                ) : (
                  <RoutesTab profile={profile} />
                )}
              </TabsContent>
            )}

            {/* Invoices Tab - Subscriber Only */}
            {profile?.subscription_status === 'active' && (
              <TabsContent value="invoices" className="space-y-6">
                <InvoicesTab profile={profile} />
              </TabsContent>
            )}

            {/* Analytics Tab - Subscriber Only */}
            {profile?.subscription_status === 'active' && (
              <TabsContent value="analytics" className="space-y-6">
                <AnalyticsTab />
              </TabsContent>
            )}

            {/* Mileage Log Tab - Subscriber Only */}
            {profile?.subscription_status === 'active' && (
              <TabsContent value="mileage" className="space-y-6">
                {Capacitor.isNativePlatform() ? (
                  <MobileMileagePage profile={profile} />
                ) : (
                  <MileageLogTab />
                )}
              </TabsContent>
            )}

            {/* Complaints Tab */}
            <TabsContent value="complaints" className="space-y-6">
              <ComplaintsTab profile={profile} />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6">
              <SettingsTab user={user} isActive={isActive} profile={profile} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
      
      {/* Document View Dialog */}
      <Dialog open={viewingDoc !== null} onOpenChange={function(open) { if (!open) setViewingDoc(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">View Document</DialogTitle>
          </DialogHeader>
          {viewingDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Document Type</p>
                  <p className="text-white capitalize">{viewingDoc.document_type.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  <p className={viewingDoc.status === 'verified' ? 'text-emerald-400' : viewingDoc.status === 'pending' ? 'text-amber-400' : 'text-red-400'}>
                    {viewingDoc.status.charAt(0).toUpperCase() + viewingDoc.status.slice(1)}
                  </p>
                </div>
                {viewingDoc.state_code && (
                  <div>
                    <p className="text-slate-500">State</p>
                    <p className="text-white">{viewingDoc.state_code}{viewingDoc.county ? ` - ${viewingDoc.county} County` : ''}</p>
                  </div>
                )}
                <div>
                  <p className="text-slate-500">Expiration Date</p>
                  <p className="text-white">{formatDate(viewingDoc.expiration_date)}</p>
                </div>
                {(viewingDoc.certification_number || viewingDoc.license_number) && (
                  <div>
                    <p className="text-slate-500">Certification/License #</p>
                    <p className="text-white">{viewingDoc.certification_number || viewingDoc.license_number}</p>
                  </div>
                )}
                {viewingDoc.issuing_authority && (
                  <div>
                    <p className="text-slate-500">Issuing Authority</p>
                    <p className="text-white">{viewingDoc.issuing_authority}</p>
                  </div>
                )}
              </div>
              
              {/* Document Preview */}
              <div className="mt-4">
                <p className="text-slate-500 text-sm mb-2">Document Preview</p>
                <div className="bg-slate-800 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  {viewingDoc.file_data ? (
                    viewingDoc.file_type?.startsWith('image/') ? (
                      <img src={viewingDoc.file_data} alt="Document" className="max-w-full max-h-[400px] object-contain rounded" />
                    ) : viewingDoc.file_type === 'application/pdf' ? (
                      <div className="text-center">
                        <FileText className="h-16 w-16 mx-auto text-slate-500 mb-2" />
                        <p className="text-slate-400 text-sm">{viewingDoc.file_name || 'PDF Document'}</p>
                        <a href={viewingDoc.file_data} download={viewingDoc.file_name || 'document.pdf'} className="text-teal-400 text-sm hover:underline mt-2 inline-block">
                          Download PDF
                        </a>
                      </div>
                    ) : (
                      <p className="text-slate-400">Preview not available</p>
                    )
                  ) : (
                    <p className="text-slate-500">Loading document...</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Subscription Required Dialog */}
      <Dialog open={showSubRequired} onOpenChange={setShowSubRequired}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <div className="text-center py-4">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-full flex items-center justify-center mb-4">
              <Crown className="h-8 w-8 text-teal-400" />
            </div>
            <DialogTitle className="text-xl text-white mb-2">PRO Feature</DialogTitle>
            <p className="text-slate-400 mb-6">
              Route Optimizer and Invoice Generator are exclusive to Servio PRO subscribers. Upgrade to unlock powerful tools that help you work smarter.
            </p>
            <div className="space-y-3">
              <Link to="/pricing">
                <Button className="w-full bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white">
                  <Crown className="h-4 w-4 mr-2" />
                  View PRO Plans
                </Button>
              </Link>
              <Button 
                variant="outline" 
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowSubRequired(false)}
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Coverage Area Item Component
function CoverageAreaItem(props) {
  var area = props.area;
  var onUpdateArea = props.onUpdateArea;
  var onUpdateFee = props.onUpdateFee;
  var onUpdateZips = props.onUpdateZips;
  var onRemove = props.onRemove;
  var formatLabel = props.formatLabel;
  
  var editingState = useState(false);
  var editing = editingState[0];
  var setEditing = editingState[1];
  
  var pricingTypeState = useState(area.pricing_type || 'fixed');
  var pricingType = pricingTypeState[0];
  var setPricingType = pricingTypeState[1];
  
  var feeState = useState(area.base_fee || 75);
  var fee = feeState[0];
  var setFee = feeState[1];
  
  var minFeeState = useState(area.min_fee || 75);
  var minFee = minFeeState[0];
  var setMinFee = minFeeState[1];
  
  var maxFeeState = useState(area.max_fee || 150);
  var maxFee = maxFeeState[0];
  var setMaxFee = maxFeeState[1];
  
  var pricingNotesState = useState(area.pricing_notes || '');
  var pricingNotes = pricingNotesState[0];
  var setPricingNotes = pricingNotesState[1];
  
  var zipsState = useState(area.zip_codes ? area.zip_codes.join(', ') : '');
  var zips = zipsState[0];
  var setZips = zipsState[1];
  
  // Rush and Same Day states
  var rushEnabledState = useState(area.rush_fee != null);
  var rushEnabled = rushEnabledState[0];
  var setRushEnabled = rushEnabledState[1];
  
  var rushFeeState = useState(area.rush_fee || 35);
  var rushFee = rushFeeState[0];
  var setRushFee = rushFeeState[1];
  
  var rushHoursState = useState(area.rush_hours || 24);
  var rushHours = rushHoursState[0];
  var setRushHours = rushHoursState[1];
  
  var sameDayEnabledState = useState(area.same_day_fee != null);
  var sameDayEnabled = sameDayEnabledState[0];
  var setSameDayEnabled = sameDayEnabledState[1];
  
  var sameDayFeeState = useState(area.same_day_fee || 75);
  var sameDayFee = sameDayFeeState[0];
  var setSameDayFee = sameDayFeeState[1];

  function handleSave() {
    var updates = {
      pricing_type: pricingType,
      base_fee: parseFloat(fee) || 75,
      min_fee: pricingType === 'variable' ? parseFloat(minFee) || 75 : null,
      max_fee: pricingType === 'variable' ? parseFloat(maxFee) || 150 : null,
      pricing_notes: pricingNotes || null,
      rush_fee: rushEnabled ? parseFloat(rushFee) || 35 : null,
      rush_hours: rushEnabled ? parseInt(rushHours) || 24 : null,
      same_day_fee: sameDayEnabled ? parseFloat(sameDayFee) || 75 : null
    };
    
    // Handle ZIP codes if it's a ZIP area
    if (area.type === 'zip' || (area.zip_codes && area.zip_codes.length > 0)) {
      var zipArray = zips.split(',').map(function(z) { return z.trim(); }).filter(function(z) { return z.length > 0; });
      updates.zip_codes = zipArray;
    }
    
    if (onUpdateArea) {
      onUpdateArea(area.area_id, updates);
    } else {
      // Fallback to old method
      onUpdateFee(area.area_id, fee);
    }
    setEditing(false);
  }
  
  function handleCancel() {
    setPricingType(area.pricing_type || 'fixed');
    setFee(area.base_fee || 75);
    setMinFee(area.min_fee || 75);
    setMaxFee(area.max_fee || 150);
    setPricingNotes(area.pricing_notes || '');
    setZips(area.zip_codes ? area.zip_codes.join(', ') : '');
    setRushEnabled(area.rush_fee != null);
    setRushFee(area.rush_fee || 35);
    setRushHours(area.rush_hours || 24);
    setSameDayEnabled(area.same_day_fee != null);
    setSameDayFee(area.same_day_fee || 75);
    setEditing(false);
  }
  
  var isZipArea = area.type === 'zip' || (area.zip_codes && area.zip_codes.length > 0);
  var currentPricingType = area.pricing_type || 'fixed';

  // Render price display based on pricing type
  function renderPriceDisplay() {
    var priceEl;
    if (currentPricingType === 'call_for_quote') {
      priceEl = (
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-400">Contact for Quote</span>
        </div>
      );
    } else if (currentPricingType === 'variable') {
      priceEl = (
        <span className="text-lg font-bold text-teal-400 font-mono">
          ${area.min_fee || 75} - ${area.max_fee || 150}
        </span>
      );
    } else {
      priceEl = (
        <span className="text-lg font-bold text-teal-400 font-mono">${area.base_fee || 75}</span>
      );
    }
    
    // Show rush/same-day indicators
    var hasRush = area.rush_fee != null;
    var hasSameDay = area.same_day_fee != null;
    
    return (
      <div className="flex items-center gap-2">
        {priceEl}
        {(hasRush || hasSameDay) && (
          <div className="flex gap-1">
            {hasRush && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400" title={'Rush: $' + area.rush_fee + ' (' + area.rush_hours + 'h)'}>
                R
              </span>
            )}
            {hasSameDay && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400" title={'Same Day: $' + area.same_day_fee}>
                SD
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Determine border color based on area type
  var borderColorClass = 'border-slate-700';
  var bgColorClass = 'bg-slate-800/60';
  if (area.type === 'state') {
    borderColorClass = 'border-violet-500/70';
    bgColorClass = 'bg-violet-500/20';
  } else if (area.type === 'county' || area.county) {
    borderColorClass = 'border-cyan-500/70';
    bgColorClass = 'bg-cyan-500/20';
  } else if (area.type === 'city' || area.city) {
    borderColorClass = 'border-emerald-500/70';
    bgColorClass = 'bg-emerald-500/20';
  } else if (area.type === 'zip' || (area.zip_codes && area.zip_codes.length > 0)) {
    borderColorClass = 'border-amber-500/70';
    bgColorClass = 'bg-amber-500/20';
  }

  if (editing) {
    return (
      <div className={'p-4 rounded-lg border ' + bgColorClass + ' ' + borderColorClass}>
        <div className="space-y-4">
          {/* Area Label */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">{formatLabel(area)}</span>
          </div>
          
          {/* ZIP codes editing (if applicable) */}
          {isZipArea && (
            <div>
              <Label htmlFor={'area-zip-codes-' + area.area_id} className="text-xs text-slate-400">ZIP Codes (comma-separated)</Label>
              <Input 
                id={'area-zip-codes-' + area.area_id}
                name="area_zip_codes"
                value={zips} 
                onChange={function(e) { setZips(e.target.value); }} 
                placeholder="79401, 79402, 79403"
                className="h-8 text-sm bg-slate-900 border-slate-600 text-white mt-1" 
              />
            </div>
          )}
          
          {/* Pricing Type Selection - Price Range only for state coverage */}
          <div>
            <Label className="text-xs text-slate-400">Pricing Type</Label>
            <div className="flex gap-2 mt-1">
              <Button 
                size="sm" 
                variant={pricingType === 'fixed' ? 'default' : 'outline'}
                onClick={function() { setPricingType('fixed'); }}
                className={pricingType === 'fixed' ? 'bg-teal-500 text-slate-900' : 'border-slate-600 text-slate-300'}
              >
                Fixed Rate
              </Button>
              {area.type === 'state' && (
                <Button 
                  size="sm" 
                  variant={pricingType === 'variable' ? 'default' : 'outline'}
                  onClick={function() { setPricingType('variable'); }}
                  className={pricingType === 'variable' ? 'bg-teal-500 text-slate-900' : 'border-slate-600 text-slate-300'}
                >
                  Price Range
                </Button>
              )}
              <Button 
                size="sm" 
                variant={pricingType === 'call_for_quote' ? 'default' : 'outline'}
                onClick={function() { setPricingType('call_for_quote'); }}
                className={pricingType === 'call_for_quote' ? 'bg-amber-500 text-slate-900' : 'border-slate-600 text-slate-300'}
              >
                Contact for Quote
              </Button>
            </div>
          </div>
          
          {/* Fee inputs based on pricing type */}
          {pricingType === 'fixed' && (
            <div>
              <Label htmlFor={'area-base-fee-' + area.area_id} className="text-xs text-slate-400">Base Fee</Label>
              <div className="relative w-32 mt-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <Input id={'area-base-fee-' + area.area_id} name="area_base_fee" type="number" step="0.01" value={fee} onChange={function(e) { setFee(e.target.value); }} className="pl-6 h-8 text-sm bg-slate-900 border-slate-600 text-white" />
              </div>
            </div>
          )}
          
          {pricingType === 'variable' && (
            <div className="flex gap-4">
              <div>
                <Label htmlFor={'area-min-fee-' + area.area_id} className="text-xs text-slate-400">Min Fee</Label>
                <div className="relative w-28 mt-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                  <Input id={'area-min-fee-' + area.area_id} name="area_min_fee" type="number" step="0.01" value={minFee} onChange={function(e) { setMinFee(e.target.value); }} className="pl-6 h-8 text-sm bg-slate-900 border-slate-600 text-white" />
                </div>
              </div>
              <div>
                <Label htmlFor={'area-max-fee-' + area.area_id} className="text-xs text-slate-400">Max Fee</Label>
                <div className="relative w-28 mt-1">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                  <Input id={'area-max-fee-' + area.area_id} name="area_max_fee" type="number" step="0.01" value={maxFee} onChange={function(e) { setMaxFee(e.target.value); }} className="pl-6 h-8 text-sm bg-slate-900 border-slate-600 text-white" />
                </div>
              </div>
            </div>
          )}
          
          {pricingType === 'call_for_quote' && (
            <div>
              <Label htmlFor={'area-pricing-notes-' + area.area_id} className="text-xs text-slate-400">Note (optional)</Label>
              <Input 
                id={'area-pricing-notes-' + area.area_id}
                name="area_pricing_notes"
                value={pricingNotes} 
                onChange={function(e) { setPricingNotes(e.target.value); }} 
                placeholder="e.g., Price varies by location within state"
                className="h-8 text-sm bg-slate-900 border-slate-600 text-white mt-1" 
              />
            </div>
          )}
          
          {/* Rush & Same Day Fees */}
          <div className="pt-3 border-t border-slate-700">
            <Label className="text-xs text-slate-400 mb-2 block">Expedited Service Options</Label>
            <div className="space-y-3">
              {/* Rush Service */}
              <div className="flex items-center gap-3">
                <Switch checked={rushEnabled} onCheckedChange={setRushEnabled} />
                <span className="text-sm text-slate-300">Rush</span>
                {rushEnabled && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Select value={String(rushHours)} onValueChange={function(v) { setRushHours(parseInt(v)); }}>
                      <SelectTrigger id={'area-rush-hours-' + area.area_id} className="w-20 h-7 bg-slate-900 border-slate-600 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="12" className="text-slate-300">12h</SelectItem>
                        <SelectItem value="24" className="text-slate-300">24h</SelectItem>
                        <SelectItem value="48" className="text-slate-300">48h</SelectItem>
                        <SelectItem value="72" className="text-slate-300">72h</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                      <Input id={'area-rush-fee-' + area.area_id} name="area_rush_fee" type="number" value={rushFee} onChange={function(e) { setRushFee(e.target.value); }} className="w-20 h-7 pl-5 bg-slate-900 border-slate-600 text-white text-xs" />
                    </div>
                  </div>
                )}
              </div>
              {/* Same Day */}
              <div className="flex items-center gap-3">
                <Switch checked={sameDayEnabled} onCheckedChange={setSameDayEnabled} />
                <span className="text-sm text-slate-300">Same Day</span>
                {sameDayEnabled && (
                  <div className="relative ml-auto">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                    <Input id={'area-same-day-fee-' + area.area_id} name="area_same_day_fee" type="number" value={sameDayFee} onChange={function(e) { setSameDayFee(e.target.value); }} className="w-20 h-7 pl-5 bg-slate-900 border-slate-600 text-white text-xs" />
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Save/Cancel buttons */}
          <div className="flex gap-2 pt-2">
            <Button size="sm" onClick={handleSave} className="bg-teal-500 hover:bg-teal-400 text-slate-900">Save Changes</Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} className="text-slate-400">Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={'p-4 rounded-lg border ' + bgColorClass + ' ' + borderColorClass}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">{formatLabel(area)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4">
          {renderPriceDisplay()}
          <Button size="sm" variant="ghost" onClick={function() { setEditing(true); }} className="h-8 text-slate-400 hover:text-white" title="Edit pricing">
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={function() { onRemove(area.area_id); }} className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" title="Remove area">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}


export default DashboardPage;
